import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { getAnalyticsConfigStatus, fetchAnalyticsEmbedToken, registerAnalyticsReport, AnalyticsEmbedConfig } from '../../services/analyticsService';
import { GlobalUser, Task, PhysicalUnit, WeeklyPlan, Contract, PaymentStatement, ProjectProgressSnapshot, LookaheadPlan, Responsible } from '../../types';
import { useSyncState } from '../../hooks/useSyncState';
import { calculateProjectProgress, calculateSCurve, truncateProgress } from '../../utils/planningUtils';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell
} from 'recharts';

interface LocalAnalyticsContainerProps {
    reportId?: string;
    currentUser?: GlobalUser;
    initialMode?: 'view' | 'edit';
    canView?: boolean;
    canDesign?: boolean;
    canModifyDesign?: boolean;
    context?: 'consumption' | 'designer';
    action?: 'view' | 'edit' | 'create';
    onReportSaved?: (reportId: string, name: string) => void;
    datasetId?: string;
    initialCategories?: string[];
    initialName?: string;
    initialScope?: 'obra' | 'multiobra' | 'corporativo';
    projectsList?: any[];
    initialProjects?: string[];
}

export const LocalAnalyticsContainer: React.FC<LocalAnalyticsContainerProps> = ({
    reportId,
    currentUser,
    initialMode = 'view',
    canView = false,
    canDesign = false,
    canModifyDesign = false,
    context = 'consumption',
    action = 'view',
    onReportSaved,
    datasetId,
    initialCategories,
    initialName,
    initialScope,
    projectsList = [],
    initialProjects
}) => {
    const { instance, accounts } = useMsal();
    const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<{ code?: string; message: string; requestId?: string } | null>(null);
    const [config, setConfig] = useState<Partial<AnalyticsEmbedConfig> & { isMocked?: boolean; name?: string; metadata?: any }>({});
    const [status] = useState(getAnalyticsConfigStatus());
    const [showDiagPanel, setShowDiagPanel] = useState<boolean>(false);
    const refreshTimerRef = useRef<any>(null);

    // --- CARGA DE DATOS LOCALES PARA EL MOTOR INTERNO ---
    const activeProjectId = typeof window !== 'undefined' ? (localStorage.getItem('selectedProjectId') || 'data') : 'data';
    const collectionName = activeProjectId !== 'data' ? `project_${activeProjectId}` : 'data';

    const [localProjectInfo] = useSyncState<any>(collectionName, 'projectInfo', {
        name: 'Obra Activa',
        startDate: '2026-01-05',
        endDate: '2026-12-31'
    });
    const [localTasks] = useSyncState<Task[]>(collectionName, 'tasks', []);
    const [localUnits] = useSyncState<PhysicalUnit[]>(collectionName, 'units', []);
    const [localMatrixData] = useSyncState<Record<string, any>>(collectionName, 'matrixData', {});
    const [localProtocolConfigs] = useSyncState<Record<string, any>>(collectionName, 'protocolConfigs', {});
    const [localSavedProtocols] = useSyncState<Record<string, any>>(collectionName, 'savedProtocols', {});
    const [localWeeklyPlans] = useSyncState<any[]>(collectionName, 'weeklyPlans', []);
    const [localContracts] = useSyncState<any[]>(collectionName, 'contracts', []);
    const [localPaymentStatements] = useSyncState<any[]>(collectionName, 'paymentStatements', []);
    const [localProgressHistory] = useSyncState<ProjectProgressSnapshot[]>(collectionName, 'progressHistory', []);
    const [localLookaheadPlans] = useSyncState<any[]>(collectionName, 'lookaheadPlans', []);
    const [localResponsibles] = useSyncState<any[]>(collectionName, 'responsibles', []);
    const [incidences] = useSyncState<any[]>(collectionName, 'incidences', []);
    const [localLevels] = useSyncState<any[]>(collectionName, 'levels', []);
    const [globalCNCGroups] = useSyncState<any[]>('data', 'cnc', []);

    const DEFAULT_COLUMNS: Record<string, string[]> = {
        kpi: ['Métrica', 'Valor', 'Detalle'],
        scurve: ['Semanas', 'Fecha', 'Estado Sincronizado (Solo % Acumulado)', '% Semanal', 'Atraso Curva S', 'Atraso Ruta Critica'],
        intermediate: ['Nombre Tarea', 'Fecha Inicio', 'Fecha Termino', 'Restricción', 'Tipo de Restricción', 'Responsable', 'Fecha Requerida', 'Estado', 'Observaciones'],
        ppc: ['Nivel', 'Unidades Físicas', 'Tarea', 'Responsable', 'L', 'M', 'X', 'J', 'V', 'S', '% Comprometido', '% Cumplimiento', 'C.N.C'],
        quality: ['Número', 'Partida', 'Estado', 'Nivel', 'Unidad Física', 'Etapa', 'Fecha Mod', 'Revisor 1', 'Revisor 2', 'Revisor 3', 'Revisor 4', 'Revisor 5'],
        subcontracts: [
            'Subcontrato', 
            'Partida', 
            'Nivel', 
            'Unidad Fisica', 
            'Cant. Contractual', 
            'Val. Unitario', 
            'Valor Total', 
            'Avance Acumulado - Cant.', 
            'Avance Acumulado - %', 
            'Avance Acumulado - Total $', 
            'Avance Anterior - Cant.', 
            'Avance Anterior - %', 
            'Avance Anterior - Total $', 
            'Avance Actual EP - Cant.', 
            'Avance Actual EP - %', 
            'Avance Actual EP - Total $'
        ]
    };

    const [selectedColumns, setSelectedColumns] = useState<Record<string, string[]>>({
        kpi: [...DEFAULT_COLUMNS.kpi],
        scurve: [...DEFAULT_COLUMNS.scurve],
        intermediate: [...DEFAULT_COLUMNS.intermediate],
        ppc: [...DEFAULT_COLUMNS.ppc],
        quality: [...DEFAULT_COLUMNS.quality],
        subcontracts: [...DEFAULT_COLUMNS.subcontracts]
    });

    // --- ESTADOS DEL DISEÑADOR LOCAL MOCK ---
    const [reportTitle, setReportTitle] = useState<string>(initialName || '');
    const [reportScope, setReportScope] = useState<'obra' | 'multiobra' | 'corporativo'>(initialScope || 'obra');
    const [selectedProjects, setSelectedProjects] = useState<string[]>(initialProjects || [activeProjectId]);
    const [reportCategories, setReportCategories] = useState<string[]>(initialCategories || []);
    const [reportDescription, setReportDescription] = useState<string>('');
    const [selectedComponents, setSelectedComponents] = useState<string[]>(() => {
        const comps = ['kpi']; // Resumen Ejecutivo (KPI) seleccionado por defecto
        if (initialCategories && initialCategories.length > 0) {
            if (initialCategories.includes('Planificación')) comps.push('scurve', 'intermediate', 'ppc');
            if (initialCategories.includes('Calidad')) comps.push('quality');
            if (initialCategories.includes('Subcontratos')) comps.push('subcontracts');
        }
        return comps;
    });
    const [savingLocal, setSavingLocal] = useState<boolean>(false);

    // --- ESTADOS DE VISUALIZACIÓN ANALÍTICA LOCAL (DASHBOARD & CAPA DAX) ---
    const [widgets, setWidgets] = useState<any[]>([]);
    const [activeEditingComponent, setActiveEditingComponent] = useState<string>('kpi');
    
    // Configuración del Widget temporal a añadir
    const [selectedVisualType, setSelectedVisualType] = useState<'bar-vertical' | 'bar-horizontal' | 'pie' | 'matrix' | null>(null);
    const [tempXAxisKey, setTempXAxisKey] = useState<string>('');
    const [tempYAxisKey, setTempYAxisKey] = useState<string>('');
    const [tempWidth, setTempWidth] = useState<'1/3' | '1/2' | 'full'>('1/2');

    // Capa DAX Local y Hoja de Cálculo
    const [runningTotalCol, setRunningTotalCol] = useState<string>('');
    const [movingAverageCol, setMovingAverageCol] = useState<string>('');
    const [movingAveragePeriod, setMovingAveragePeriod] = useState<number>(3);
    const [deltaCol, setDeltaCol] = useState<string>('');
    
    // Concatenador con Delimitador Personalizado
    const [concatColA, setConcatColA] = useState<string>('');
    const [concatColB, setConcatColB] = useState<string>('');
    const [concatDelimiter, setConcatDelimiter] = useState<string>(' - ');

    // Filtros múltiples avanzados AND/OR
    const [filterRules, setFilterRules] = useState<any[]>([]);
    const [filterJoinType, setFilterJoinType] = useState<'AND' | 'OR'>('AND');
    
    // Reglas de Formato Condicional
    const [cfRules, setCfRules] = useState<any[]>([]);

    // --- ESTADOS DEL VISOR LOCAL MOCK ---
    const [localActiveTab, setLocalActiveTab] = useState<string>('resumen');

    // Sincronizar props iniciales con estados locales cuando cambian
    useEffect(() => {
        if (initialName) {
            setReportTitle(initialName);
        }
    }, [initialName]);

    useEffect(() => {
        if (initialScope) {
            setReportScope(initialScope);
        }
    }, [initialScope]);

    useEffect(() => {
        if (initialCategories && initialCategories.length > 0) {
            setReportCategories(initialCategories);
        }
    }, [initialCategories]);

    // Inicializar el diseñador con datos si es modo edición/creación
    useEffect(() => {
        if (config.name) {
            setReportTitle(config.name);
        }
        if (config.metadata?.categories) {
            setReportCategories(config.metadata.categories);
        } else if (config.metadata?.category) {
            setReportCategories([config.metadata.category]);
        }
        if (config.metadata?.description) {
            setReportDescription(config.metadata.description);
        }
        if (config.metadata?.components) {
            setSelectedComponents(config.metadata.components);
        }
        
        // Cargar widgets analíticos multigráficos
        if (config.metadata?.widgets) {
            setWidgets(config.metadata.widgets);
        } else {
            setWidgets([]);
        }

        // Cargar operaciones y lógica analítica avanzada (DAX)
        if (config.metadata?.daxOperations) {
            const ops = config.metadata.daxOperations;
            setRunningTotalCol(ops.runningTotalCol || '');
            setMovingAverageCol(ops.movingAverageCol || '');
            setMovingAveragePeriod(ops.movingAveragePeriod || 3);
            setDeltaCol(ops.deltaCol || '');
            setConcatColA(ops.concatColA || '');
            setConcatColB(ops.concatColB || '');
            setConcatDelimiter(ops.concatDelimiter || ' - ');
            setFilterRules(ops.filterRules || []);
            setFilterJoinType(ops.filterJoinType || 'AND');
            setCfRules(ops.cfRules || []);
        } else {
            setRunningTotalCol('');
            setMovingAverageCol('');
            setMovingAveragePeriod(3);
            setDeltaCol('');
            setConcatColA('');
            setConcatColB('');
            setConcatDelimiter(' - ');
            setFilterRules([]);
            setFilterJoinType('AND');
            setCfRules([]);
        }

        if (config.metadata?.selectedColumns) {
            setSelectedColumns(config.metadata.selectedColumns);
        } else {
            setSelectedColumns({
                scurve: [...DEFAULT_COLUMNS.scurve],
                intermediate: [...DEFAULT_COLUMNS.intermediate],
                ppc: [...DEFAULT_COLUMNS.ppc],
                quality: [...DEFAULT_COLUMNS.quality],
                subcontracts: [...DEFAULT_COLUMNS.subcontracts]
            });
        }
    }, [config]);

    const getFreshMsalToken = async () => {
        const account = instance.getActiveAccount() || accounts[0];
        if (account) {
            try {
                const response = await instance.acquireTokenSilent({
                    scopes: ["User.Read"],
                    account: account
                });
                localStorage.setItem('klyd_msal_access_token', response.accessToken);
                return response.accessToken;
            } catch (e) {
                console.warn("Silent token acquisition failed:", e);
            }
        }
        return null;
    };

    const isAdmin = currentUser?.globalRole === 'Administrador' || currentUser?.globalRole === ('SYSADMIN' as any);

    const scheduleTokenRefresh = (expirationTimeStr?: string) => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }

        if (!expirationTimeStr || config.isMocked) return;

        const expirationDate = new Date(expirationTimeStr);
        const now = new Date();
        const timeToExpiration = expirationDate.getTime() - now.getTime();

        const leadTime = 5 * 60 * 1000;
        let refreshIn = timeToExpiration - leadTime;
        if (refreshIn < 0) {
            refreshIn = Math.max(30 * 1000, timeToExpiration - 10 * 1000);
        }

        console.log(`Scheduling token refresh in ${(refreshIn / 1000).toFixed(0)} seconds.`);

        refreshTimerRef.current = setTimeout(() => {
            console.log("Triggering scheduled Embed Token refresh...");
            refreshToken();
        }, refreshIn);
    };

    const refreshToken = async () => {
        if (config.isMocked) return;
        try {
            await getFreshMsalToken();
            const effectiveMode = (mode === 'edit' && !isAdmin && !canModifyDesign) ? 'view' : mode;
            const res = await fetchAnalyticsEmbedToken(reportId, effectiveMode, context, action, datasetId);
            setConfig(res);
            scheduleTokenRefresh(res.expiresAt);
        } catch (err: any) {
            console.error("Failed to refresh analytics token, retrying in 30 seconds:", err);
            refreshTimerRef.current = setTimeout(refreshToken, 30 * 1000);
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const effectiveMode = (mode === 'edit' && !isAdmin && !canModifyDesign) ? 'view' : mode;
        getFreshMsalToken().then(() => {
            fetchAnalyticsEmbedToken(reportId, effectiveMode, context, action, datasetId)
                .then((res) => {
                    if (isMounted) {
                        // Forzar isMocked a true para usar siempre el visualizador React nativo
                        setConfig({ ...res, isMocked: true });
                        scheduleTokenRefresh(res.expiresAt);
                        setLoading(false);
                    }
                })
                .catch((err: any) => {
                    console.error("Error fetching analytics config:", err);
                    if (isMounted) {
                        setError({
                            code: err.code || 'CONNECTION_ERROR',
                            message: err.message || 'Error al establecer la conexión segura con el servidor analítico.',
                            requestId: err.requestId
                        });
                        setLoading(false);
                    }
                });
        });

        return () => {
            isMounted = false;
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
                refreshTimerRef.current = null;
            }
        };
    }, [reportId, mode, action, datasetId]);

    // --- CÁLCULOS ANALÍTICOS LOCALES ---
    const formatCLP = (val: number) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(val);
    };

    const projectProgress = useMemo(() => {
        if (!localTasks || localTasks.length === 0) return 0;
        try {
            return calculateProjectProgress(
                localTasks,
                localUnits,
                localMatrixData,
                localProtocolConfigs,
                localProjectInfo,
                localSavedProtocols,
                localWeeklyPlans
            );
        } catch (e) {
            console.error(e);
            return 0;
        }
    }, [localTasks, localUnits, localMatrixData, localProtocolConfigs, localProjectInfo, localSavedProtocols, localWeeklyPlans]);

    const ppcMetrics = useMemo(() => {
        let executed = 0;
        let notExecuted = 0;
        let missing = 0;

        localWeeklyPlans.forEach((plan: any) => {
            if (plan.status === 'Cerrada' && Array.isArray(plan.allocations)) {
                plan.allocations.forEach((alloc: any) => {
                    if (alloc.isWeeklySubtask === true) return; // Regla de negocio de AGENTS.md
                    if (alloc.executed === true) {
                        executed++;
                    } else if (alloc.executed === false) {
                        notExecuted++;
                    } else {
                        missing++;
                    }
                });
            }
        });

        const totalValid = executed + notExecuted;
        const ppcValid = totalValid > 0 ? (executed / totalValid) * 100 : 0;
        const totalGross = executed + notExecuted + missing;
        const ppcGross = totalGross > 0 ? (executed / totalGross) * 100 : 0;

        return {
            ppcValid,
            ppcGross,
            executed,
            notExecuted,
            missing,
            totalValid
        };
    }, [localWeeklyPlans]);

    const qualityMetrics = useMemo(() => {
        let openCount = 0;
        let closedCount = 0;
        let rejectedCount = 0;

        Object.values(localSavedProtocols).forEach((p: any) => {
            if (p) {
                const s = (p.status || '').toLowerCase();
                if (s.includes('cerr') || s === 'closed' || s === 'approved') {
                    closedCount++;
                } else if (s.includes('rech') || s === 'rejected' || s === 'failed') {
                    rejectedCount++;
                } else {
                    openCount++;
                }
            }
        });

        const total = openCount + closedCount + rejectedCount;
        return { total, openCount, closedCount, rejectedCount };
    }, [localSavedProtocols]);

    const financialMetrics = useMemo(() => {
        let contracted = 0;
        let approved = 0;
        let retentions = 0;

        localContracts.forEach((c: any) => {
            if (c && c.status !== 'Anulado') {
                contracted += (c.amount || 0);
            }
        });

        localPaymentStatements.forEach((ps: any) => {
            if (ps && ps.status === 'Aprobado') {
                approved += (ps.netAmount || 0);
                retentions += (ps.retentionAmount || (ps.netAmount * 0.05) || 0);
            }
        });

        return { contracted, approved, retentions };
    }, [localContracts, localPaymentStatements]);

    const kpiRows = useMemo(() => [
        { 'Métrica': 'Avance Físico', 'Valor': `${projectProgress.toFixed(2)}%`, 'Detalle': 'Global Ponderado' },
        { 'Métrica': 'PPC Válido', 'Valor': `${ppcMetrics.ppcValid.toFixed(1)}%`, 'Detalle': 'Last Planner Standard' },
        { 'Métrica': 'Pagos Aprobados', 'Valor': formatCLP(financialMetrics.approved), 'Detalle': 'Neto Subcontratos' },
        { 'Métrica': 'Garantía Retenida', 'Valor': formatCLP(financialMetrics.retentions), 'Detalle': 'Retención Fija 5%' }
    ], [projectProgress, ppcMetrics, financialMetrics]);

    const scurvePoints = useMemo(() => {
        if (!localTasks || localTasks.length === 0) return [];
        try {
            return calculateSCurve(localTasks, localProgressHistory);
        } catch (e) {
            console.error(e);
            return [];
        }
    }, [localTasks, localProgressHistory]);

    // PPC por Semana
    const ppcTrendData = useMemo(() => {
        const sortedPlans = [...localWeeklyPlans]
            .filter(p => p.status === 'Cerrada' && p.weekRange)
            .sort((a, b) => (a.weekRange || '').localeCompare(b.weekRange || ''));

        return sortedPlans.map((p, idx) => {
            let executed = 0;
            let total = 0;
            p.allocations?.forEach((alloc: any) => {
                if (alloc.isWeeklySubtask === true) return;
                if (alloc.executed === true) {
                    executed++;
                    total++;
                } else if (alloc.executed === false) {
                    total++;
                }
            });
            const pct = total > 0 ? (executed / total) * 100 : 0;
            return {
                name: `Sem ${idx + 1}`,
                rango: p.weekRange,
                PPC: Number(pct.toFixed(1))
            };
        });
    }, [localWeeklyPlans]);

    // Datos Financieros Subcontratos
    const subcontractChartData = useMemo(() => {
        return localContracts.map(c => {
            const approvedAmount = localPaymentStatements
                .filter(ps => ps.contractId === c.id && ps.status === 'Aprobado')
                .reduce((sum, ps) => sum + (ps.netAmount || 0), 0);
            return {
                name: c.subcontractorName?.substring(0, 15) || 'Subcontratista',
                Contratado: c.amount || 0,
                Visado: approvedAmount
            };
        }).slice(0, 8); // Top 8 subcontratos
    }, [localContracts, localPaymentStatements]);

    // Mapeo dinámico de datos para Tablas Personalizadas (Excel)

    // 1. Planificación Maestra (Gantt)
    const scurveRows = useMemo(() => {
        return scurvePoints.map((pt, idx) => {
            const prevPlan = idx > 0 ? scurvePoints[idx - 1].plan : 0;
            const weeklyPlanPct = pt.plan - prevPlan;
            return {
                'Semanas': `Semana ${idx + 1}`,
                'Fecha': pt.date || pt.name,
                'Estado Sincronizado (Solo % Acumulado)': pt.real !== null ? `${pt.real.toFixed(1)}%` : '-',
                '% Semanal': `${weeklyPlanPct.toFixed(1)}%`,
                'Atraso Curva S': pt.real !== null && pt.plan > pt.real ? `${(pt.plan - pt.real).toFixed(1)}%` : '0.0%',
                'Atraso Ruta Critica': pt.real !== null && pt.plan > pt.real ? 'Retrasado' : 'Al día'
            };
        });
    }, [scurvePoints]);

    // 2. Planificación Intermedia (Lookahead)
    const intermediateRows = useMemo(() => {
        const rows: any[] = [];
        localLookaheadPlans.forEach(plan => {
            if (plan && Array.isArray(plan.constraints)) {
                plan.constraints.forEach(c => {
                    const task = localTasks.find(t => t.id === c.taskId);
                    const responsible = localResponsibles.find(r => r.id === c.responsibleId);
                    rows.push({
                        'Nombre Tarea': task ? task.name : 'Tarea no encontrada',
                        'Fecha Inicio': task?.start || '-',
                        'Fecha Termino': task?.end || '-',
                        'Restricción': c.description || 'Sin descripción',
                        'Tipo de Restricción': c.typeId || 'General',
                        'Responsable': responsible ? responsible.name : 'Sin asignar',
                        'Fecha Requerida': c.requiredDate || '-',
                        'Estado': c.status || 'Pendiente',
                        'Observaciones': c.observations || ''
                    });
                });
            }
        });
        return rows;
    }, [localLookaheadPlans, localTasks, localResponsibles]);

    // 3. Planificación Semanal (LPS)
    const ppcRows = useMemo(() => {
        const rows: any[] = [];
        localWeeklyPlans.forEach(plan => {
            if (plan && Array.isArray(plan.allocations)) {
                plan.allocations.forEach(alloc => {
                    if (alloc.isWeeklySubtask === true) return;
                    const task = localTasks.find(t => t.id === alloc.taskId);
                    const unit = localUnits.find(u => u.id === alloc.unitId);
                    const responsible = localResponsibles.find(r => r.id === alloc.responsibleId);
                    const d = alloc.days || {};
                    
                    const level = localLevels.find(l => l.id === unit?.levelId);
                    const levelName = level ? level.name : (unit?.levelId || 'Nivel General');

                    const causeGroup = globalCNCGroups.find(g => 
                        g.causes && g.causes.some((c: any) => c.id === alloc.cncId)
                    );
                    const cause = causeGroup?.causes.find((c: any) => c.id === alloc.cncId);
                    const cncName = cause ? `${cause.id}: ${cause.name}` : (alloc.cncId || '-');

                    rows.push({
                        'Nivel': levelName,
                        'Unidades Físicas': unit ? unit.name : 'Unidad',
                        'Tarea': task ? task.name : (alloc.additionalTaskName || 'Asignación'),
                        'Responsable': responsible ? responsible.name : 'Sin asignar',
                        'L': d['L'] ? '✓' : '',
                        'M': d['M'] ? '✓' : '',
                        'X': d['X'] ? '✓' : '',
                        'J': d['J'] ? '✓' : '',
                        'V': d['V'] ? '✓' : '',
                        'S': d['S'] ? '✓' : '',
                        '% Comprometido': `${alloc.percentComprometido || 100}%`,
                        '% Cumplimiento': alloc.executed === true ? '100%' : alloc.executed === false ? '0%' : '-',
                        'C.N.C': cncName
                    });
                });
            }
        });
        return rows;
    }, [localWeeklyPlans, localTasks, localUnits, localResponsibles, localLevels, globalCNCGroups]);

    // 4. Control de Calidad
    const qualityRows = useMemo(() => {
        return Object.values(localSavedProtocols).map((p: any) => {
            const unit = localUnits.find(u => u.id === p.unitId);
            const task = localTasks.find(t => t.id === p.taskId);
            const lastHistory = p.history && p.history.length > 0 ? p.history[p.history.length - 1] : null;
            
            const level = localLevels.find(l => l.id === unit?.levelId);
            const levelName = level ? level.name : (unit?.levelId || 'Nivel General');

            return {
                'Número': p.id,
                'Partida': p.protocolName || p.taskName || task?.name || 'Protocolo de Control',
                'Estado': p.status || 'Abierto',
                'Nivel': levelName,
                'Unidad Física': unit ? unit.name : 'Unidad',
                'Etapa': `Revisión ${p.currentReviewerIndex + 1}`,
                'Fecha Mod': lastHistory ? new Date(lastHistory.date).toLocaleDateString('es-CL') : new Date(p.creationDate).toLocaleDateString('es-CL'),
                'Revisor 1': p.signatures && p.signatures[0] ? 'Firmado' : 'Pendiente',
                'Revisor 2': p.signatures && p.signatures[1] ? 'Firmado' : 'Pendiente',
                'Revisor 3': p.signatures && p.signatures[2] ? 'Firmado' : 'Pendiente',
                'Revisor 4': p.signatures && p.signatures[3] ? 'Firmado' : 'Pendiente',
                'Revisor 5': p.signatures && p.signatures[4] ? 'Firmado' : 'Pendiente'
            };
        });
    }, [localSavedProtocols, localUnits, localTasks, localLevels]);

    // 5. Subcontratos
    const subcontractRows = useMemo(() => {
        const rows: any[] = [];
        localContracts.forEach(contract => {
            if (contract && Array.isArray(contract.items)) {
                contract.items.forEach(item => {
                    const totalApprovedQty = localPaymentStatements
                        .filter(ps => ps.contractId === contract.id && ps.status === 'Aprobado')
                        .reduce((sum, ps) => {
                            const matchedItem = ps.items?.find(i => i.id === item.id);
                            return sum + (matchedItem?.paidAmount || 0);
                        }, 0);
                    const unitPrice = item.unitPrice ? parseFloat(item.unitPrice.toString().replace(/[^0-9.-]+/g,"")) : 0;
                    const matchedDetails = item.details?.[0];
                    const accumulatedQty = totalApprovedQty;
                    const accumulatedPct = ((totalApprovedQty / (item.quantity || 1)) * 100).toFixed(1) + '%';
                    const accumulatedTotal = totalApprovedQty * unitPrice;

                    const currentEPQty = item.paidAmount || 0;
                    const currentEPPct = (((item.paidAmount || 0) / (item.quantity || 1)) * 100).toFixed(1) + '%';
                    const currentEPTotal = (item.paidAmount || 0) * unitPrice;

                    const previousQty = Math.max(0, totalApprovedQty - currentEPQty);
                    const previousPct = ((previousQty / (item.quantity || 1)) * 100).toFixed(1) + '%';
                    const previousTotal = previousQty * unitPrice;

                    rows.push({
                        'Subcontrato': contract.subcontractorName || 'Subcontratista',
                        'Partida': item.description,
                        'Nivel': matchedDetails?.unitName || 'General',
                        'Unidad Fisica': matchedDetails?.unitName || '-',
                        'Cant. Contractual': item.quantity || 1,
                        'Val. Unitario': formatCLP(unitPrice),
                        'Valor Total': formatCLP(item.totalValue || (item.quantity * unitPrice)),
                        'Avance Acumulado - Cant.': accumulatedQty.toFixed(1),
                        'Avance Acumulado - %': accumulatedPct,
                        'Avance Acumulado - Total $': formatCLP(accumulatedTotal),
                        'Avance Anterior - Cant.': previousQty.toFixed(1),
                        'Avance Anterior - %': previousPct,
                        'Avance Anterior - Total $': formatCLP(previousTotal),
                        'Avance Actual EP - Cant.': currentEPQty.toFixed(1),
                        'Avance Actual EP - %': currentEPPct,
                        'Avance Actual EP - Total $': formatCLP(currentEPTotal)
                    });
                });
            }
        });
        return rows;
    }, [localContracts, localPaymentStatements]);

    const renderCustomDataTable = (componentId: string, dataRows: any[]) => {
        const columns = selectedColumns[componentId] && selectedColumns[componentId].length > 0
            ? selectedColumns[componentId]
            : (DEFAULT_COLUMNS[componentId] || []);

        if (dataRows.length === 0) {
            return (
                <div className="p-8 text-center text-xs text-slate-450 dark:text-slate-500 italic bg-slate-50/50 dark:bg-slate-800/10 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
                    No hay registros de datos para mostrar.
                </div>
            );
        }

        return (
            <div className="w-full overflow-x-auto rounded-2xl border border-slate-200/60 dark:border-slate-700/60 bg-white/50 dark:bg-slate-900/40 shadow-sm mt-4">
                <table className="w-full border-collapse text-left text-xs">
                    <thead>
                        <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200/60 dark:border-slate-700/60">
                            {columns.map((col, idx) => (
                                <th key={idx} className="px-4 py-3 font-extrabold uppercase tracking-wider text-[10px] text-slate-400 dark:text-slate-500">
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50 text-slate-700 dark:text-slate-200">
                        {dataRows.map((row, rowIdx) => (
                            <tr key={rowIdx} className="hover:bg-slate-50/40 dark:hover:bg-slate-800/20 transition-colors">
                                {columns.map((col, colIdx) => (
                                    <td key={colIdx} className="px-4 py-3 whitespace-nowrap">
                                        {row[col] !== undefined ? row[col] : '-'}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const isEditAuthorized = config.permissions === 'ReadWrite' || config.isMocked;
    const showPermissionError = mode === 'edit' && !isEditAuthorized && !canDesign;
    const hasWarningBanner = !isAdmin && !canModifyDesign && mode === 'edit' && canDesign && !config.isMocked;

    // --- ACCIÓN DE EXPORTACIÓN ---
    const handleExportPDF = () => {
        window.print();
    };

    const handleExportHTML = () => {
        const buildHTMLTable = (componentId: string, rows: any[]) => {
            const columns = selectedColumns[componentId] && selectedColumns[componentId].length > 0
                ? selectedColumns[componentId]
                : (DEFAULT_COLUMNS[componentId] || []);
            
            if (rows.length === 0) {
                return '<p style="color: #64748b; font-style: italic; font-size: 11px;">No hay registros de datos disponibles.</p>';
            }

            return `
            <div style="overflow-x: auto; margin-top: 12px; border: 1px solid #e2e8f0; border-radius: 8px;">
                <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 11px;">
                    <thead>
                        <tr style="background-color: #f8fafc; border-bottom: 1px solid #e2e8f0;">
                            ${columns.map(c => `<th style="padding: 10px; font-weight: bold; color: #475569; border-bottom: 1px solid #e2e8f0;">${c}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${rows.map(row => `
                            <tr style="border-bottom: 1px solid #f1f5f9;">
                                ${columns.map(col => `<td style="padding: 10px; color: #334155;">${row[col] !== undefined ? row[col] : '-'}</td>`).join('')}
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>`;
        };

        const htmlContent = `<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <title>Reporte: ${config.name || 'Reporte de Gestión'}</title>
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 40px; color: #1e293b; background-color: #f8fafc; }
        .container { max-width: 1100px; margin: 0 auto; background: white; padding: 40px; border-radius: 16px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); }
        h1 { color: #136dec; border-bottom: 2px solid #e2e8f0; padding-bottom: 12px; font-size: 22px; display: flex; align-items: center; gap: 8px; margin-top: 0; }
        .meta { color: #64748b; font-size: 12px; margin-bottom: 24px; }
        .kpi-container { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 32px; }
        .kpi-card { border: 1px solid #e2e8f0; padding: 18px; border-radius: 12px; background: #f8fafc; }
        .kpi-title { font-size: 10px; text-transform: uppercase; font-weight: bold; color: #64748b; margin-bottom: 4px; }
        .kpi-val { font-size: 20px; font-weight: 900; color: #136dec; }
        .section-card { border: 1px solid #e2e8f0; padding: 24px; border-radius: 16px; background: white; margin-bottom: 32px; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
        .section-title { font-size: 13px; font-weight: bold; color: #1e293b; margin-bottom: 16px; border-left: 4px solid #136dec; padding-left: 10px; text-transform: uppercase; }
        .chart-container { height: 260px; position: relative; margin-bottom: 24px; }
    </style>
</head>
<body>
    <div class="container">
        <h1>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: middle;">
                <rect x="3" y="3" width="18" height="18" rx="2" fill="#136dec"/>
                <path d="M7 16V14H9V16H7ZM11 16V10H13V16H11ZM15 16V12H17V16H15Z" fill="white"/>
            </svg>
            Reporte de Gestión de Obra: ${config.name || 'Reporte Local'}
        </h1>
        <div class="meta">Generado el: ${new Date().toLocaleString('es-CL')} | K-Lyapp ConstructOS</div>
        
        ${selectedComponents.includes('kpi') ? `
        <div class="kpi-container">
            <div class="kpi-card">
                <div class="kpi-title">Avance Físico Global</div>
                <div class="kpi-val">${projectProgress.toFixed(2)}%</div>
            </div>
            <div class="kpi-card">
                <div class="kpi-title">PPC Válido (LPS)</div>
                <div class="kpi-val">${ppcMetrics.ppcValid.toFixed(1)}%</div>
            </div>
            <div class="kpi-card">
                <div class="kpi-title">Pagos Subcontratos</div>
                <div class="kpi-val">${formatCLP(financialMetrics.approved)}</div>
            </div>
            <div class="kpi-card">
                <div class="kpi-title">Garantía Retenida (5%)</div>
                <div class="kpi-val">${formatCLP(financialMetrics.retentions)}</div>
            </div>
        </div>` : ''}

        ${selectedComponents.includes('scurve') ? `
        <div class="section-card">
            <div class="section-title">Planificación Maestra (Curva S)</div>
            <div class="chart-container">
                <canvas id="scurveChart"></canvas>
            </div>
            ${buildHTMLTable('scurve', scurveRows)}
        </div>` : ''}

        ${selectedComponents.includes('intermediate') ? `
        <div class="section-card">
            <div class="section-title">Planificación Intermedia (Lookahead)</div>
            ${buildHTMLTable('intermediate', intermediateRows)}
        </div>` : ''}

        ${selectedComponents.includes('ppc') ? `
        <div class="section-card">
            <div class="section-title">Planificación Semanal (PPC LPS)</div>
            <div class="chart-container" style="height: 240px;">
                <canvas id="ppcChart"></canvas>
            </div>
            ${buildHTMLTable('ppc', ppcRows)}
        </div>` : ''}

        ${selectedComponents.includes('quality') ? `
        <div class="section-card">
            <div class="section-title">Control de Calidad</div>
            <div style="display: flex; gap: 40px; margin-bottom: 24px; align-items: center;">
                <div style="width: 180px; height: 180px; position: relative;">
                    <canvas id="qualityChart"></canvas>
                </div>
                <div style="flex: 1; font-size: 12px; line-height: 1.8;">
                    <div><strong>Aprobados:</strong> ${qualityMetrics.closedCount} (${(qualityMetrics.total > 0 ? (qualityMetrics.closedCount/qualityMetrics.total)*100 : 0).toFixed(1)}%)</div>
                    <div><strong>Abiertos/En Proceso:</strong> ${qualityMetrics.openCount} (${(qualityMetrics.total > 0 ? (qualityMetrics.openCount/qualityMetrics.total)*100 : 0).toFixed(1)}%)</div>
                    <div><strong>Rechazados:</strong> ${qualityMetrics.rejectedCount} (${(qualityMetrics.total > 0 ? (qualityMetrics.rejectedCount/qualityMetrics.total)*100 : 0).toFixed(1)}%)</div>
                </div>
            </div>
            ${buildHTMLTable('quality', qualityRows)}
        </div>` : ''}

        ${selectedComponents.includes('subcontracts') ? `
        <div class="section-card">
            <div class="section-title">Subcontrato</div>
            <div class="chart-container">
                <canvas id="subcontractsChart"></canvas>
            </div>
            ${buildHTMLTable('subcontracts', subcontractRows)}
        </div>` : ''}
    </div>

    <script>
        const scurveData = ${JSON.stringify(scurvePoints)};
        const ppcData = ${JSON.stringify(ppcTrendData)};
        const qualityData = {
            closed: ${qualityMetrics.closedCount},
            open: ${qualityMetrics.openCount},
            rejected: ${qualityMetrics.rejectedCount}
        };
        const subcontractsData = ${JSON.stringify(subcontractChartData)};

        if (document.getElementById('scurveChart')) {
            new Chart(document.getElementById('scurveChart').getContext('2d'), {
                type: 'line',
                data: {
                    labels: scurveData.map(d => d.name),
                    datasets: [
                        {
                            label: 'Progreso Programado',
                            data: scurveData.map(d => d.plan),
                            borderColor: '#3b82f6',
                            backgroundColor: 'rgba(59, 130, 246, 0.05)',
                            fill: true,
                            tension: 0.3,
                            borderWidth: 2
                        },
                        {
                            label: 'Progreso Real',
                            data: scurveData.map(d => d.real),
                            borderColor: '#10b981',
                            backgroundColor: 'rgba(16, 185, 129, 0.05)',
                            fill: true,
                            tension: 0.3,
                            borderWidth: 2
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: { y: { min: 0, max: 100 } }
                }
            });
        }

        if (document.getElementById('ppcChart')) {
            new Chart(document.getElementById('ppcChart').getContext('2d'), {
                type: 'bar',
                data: {
                    labels: ppcData.map(d => d.name),
                    datasets: [{
                        label: 'Porcentaje PPC',
                        data: ppcData.map(d => d.PPC),
                        backgroundColor: ppcData.map(d => d.PPC >= 85 ? '#10b981' : '#f59e0b'),
                        borderRadius: 4
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: { y: { min: 0, max: 100 } }
                }
            });
        }

        if (document.getElementById('qualityChart')) {
            new Chart(document.getElementById('qualityChart').getContext('2d'), {
                type: 'doughnut',
                data: {
                    labels: ['Cerrados', 'Abiertos', 'Rechazados'],
                    datasets: [{
                        data: [qualityData.closed, qualityData.open, qualityData.rejected],
                        backgroundColor: ['#10b981', '#3b82f6', '#f43f5e']
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } }
                }
            });
        }

        if (document.getElementById('subcontractsChart')) {
            new Chart(document.getElementById('subcontractsChart').getContext('2d'), {
                type: 'bar',
                data: {
                    labels: subcontractsData.map(d => d.name),
                    datasets: [
                        {
                            label: 'Monto Contratado',
                            data: subcontractsData.map(d => d.Contratado),
                            backgroundColor: '#3b82f6'
                        },
                        {
                            label: 'Monto Aprobado',
                            data: subcontractsData.map(d => d.Visado),
                            backgroundColor: '#10b981'
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false
                }
            });
        }
    </script>
</body>
</html>`;
        const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Reporte_Personalizado_${config.name || 'Local'}.html`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportCSV = () => {
        let csvContent = "\uFEFF";
        
        if (selectedComponents.includes('kpi')) {
            csvContent += "=== RESUMEN EJECUTIVO ===\n";
            csvContent += "Módulo,Métrica,Valor\n";
            csvContent += `Planificación,Avance Físico Global,${projectProgress.toFixed(2)}%\n`;
            csvContent += `Planificación,Cumplimiento PPC Válido,${ppcMetrics.ppcValid.toFixed(2)}%\n`;
            csvContent += `Subcontratos,Total Pagado Visado,${financialMetrics.approved}\n`;
            csvContent += `Subcontratos,Total Garantía Retenida,${financialMetrics.retentions}\n\n`;
        }

        const appendCSVSection = (componentId: string, title: string, rows: any[]) => {
            if (!selectedComponents.includes(componentId)) return;
            const columns = selectedColumns[componentId] && selectedColumns[componentId].length > 0
                ? selectedColumns[componentId]
                : (DEFAULT_COLUMNS[componentId] || []);
            
            csvContent += `=== ${title.toUpperCase()} ===\n`;
            csvContent += columns.map(c => `"${c}"`).join(",") + "\n";
            rows.forEach(row => {
                csvContent += columns.map(col => `"${row[col] !== undefined ? row[col].toString().replace(/"/g, '""') : '-'}"`).join(",") + "\n";
            });
            csvContent += "\n";
        };

        appendCSVSection('scurve', 'Planificación Maestra (Gantt)', scurveRows);
        appendCSVSection('intermediate', 'Planificación Intermedia', intermediateRows);
        appendCSVSection('ppc', 'Planificación Semanal (LPS)', ppcRows);
        appendCSVSection('quality', 'Control de Calidad', qualityRows);
        appendCSVSection('subcontracts', 'Subcontrato', subcontractRows);

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Reporte_Personalizado_${config.name || 'Local'}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSaveLocalReport = async () => {
        if (!reportTitle.trim()) {
            alert("Por favor, ingrese un nombre para el reporte.");
            return;
        }
        setSavingLocal(true);
        try {
            const localReportId = reportId && reportId.startsWith('local-') 
                ? reportId 
                : `local-${Math.random().toString(36).substring(2, 11)}-${Date.now().toString(36)}`;
            
            await registerAnalyticsReport(
                localReportId,
                reportTitle,
                reportScope,
                [activeProjectId],
                {
                    category: reportCategories[0] || 'Planificación',
                    categories: reportCategories,
                    description: reportDescription || 'Reporte interactivo local.',
                    components: selectedComponents,
                    selectedColumns: selectedColumns,
                    widgets: widgets,
                    daxOperations: {
                        runningTotalCol,
                        movingAverageCol,
                        movingAveragePeriod,
                        deltaCol,
                        concatColA,
                        concatColB,
                        concatDelimiter,
                        filterRules,
                        filterJoinType,
                        cfRules
                    }
                }
            );

            if (onReportSaved) {
                onReportSaved(localReportId, reportTitle);
            }
        } catch (err: any) {
            console.error(err);
            alert(`Error al guardar reporte local: ${err.message || 'Error del servidor'}`);
        } finally {
            setSavingLocal(false);
        }
    };

    const toggleComponent = (comp: string) => {
        setSelectedComponents(prev => {
            const updated = prev.includes(comp)
                ? prev.filter(c => c !== comp)
                : [...prev, comp];
            
            if (!prev.includes(comp)) {
                setActiveEditingComponent(comp);
            } else if (activeEditingComponent === comp) {
                const remaining = updated.filter(c => c !== comp);
                setActiveEditingComponent(remaining.length > 0 ? remaining[0] : 'kpi');
            }
            return updated;
        });
    };

    // --- PROCESAMIENTO ANALÍTICO LOCAL (DAX) ---
    const processCalculations = (componentId: string, sourceRows: any[]) => {
        let resultRows = [...sourceRows];

        // 1. Filtros avanzados AND/OR
        if (filterRules.length > 0) {
            resultRows = resultRows.filter(row => {
                const matches = filterRules.map(rule => {
                    const cellVal = String(row[rule.column] || '').toLowerCase();
                    const filterVal = String(rule.value).toLowerCase();
                    
                    if (rule.operator === 'equals') return cellVal === filterVal;
                    if (rule.operator === 'contains') return cellVal.includes(filterVal);
                    if (rule.operator === 'greater') {
                        const numCell = parseFloat(cellVal.replace(/[^\d.-]/g, ''));
                        const numFilter = parseFloat(filterVal);
                        return !isNaN(numCell) && !isNaN(numFilter) && numCell > numFilter;
                    }
                    if (rule.operator === 'less') {
                        const numCell = parseFloat(cellVal.replace(/[^\d.-]/g, ''));
                        const numFilter = parseFloat(filterVal);
                        return !isNaN(numCell) && !isNaN(numFilter) && numCell < numFilter;
                    }
                    return true;
                });

                if (filterJoinType === 'AND') {
                    return matches.every(m => m === true);
                } else {
                    return matches.some(m => m === true);
                }
            });
        }

        // 2. Concatenador con Delimitador Personalizado
        if (concatColA && concatColB) {
            resultRows = resultRows.map(row => {
                const valA = row[concatColA] !== undefined ? String(row[concatColA]) : '';
                const valB = row[concatColB] !== undefined ? String(row[concatColB]) : '';
                return {
                    ...row,
                    'Concatenado': `${valA}${concatDelimiter}${valB}`
                };
            });
        }

        // 3. Suma Acumulada (Running Total)
        if (runningTotalCol) {
            let cumulative = 0;
            resultRows = resultRows.map(row => {
                const rawVal = String(row[runningTotalCol] || '').replace(/[^\d.-]/g, '');
                const num = parseFloat(rawVal);
                cumulative += isNaN(num) ? 0 : num;
                const isCurrency = runningTotalCol.includes('$') || runningTotalCol.toLowerCase().includes('valor') || runningTotalCol.toLowerCase().includes('monto') || runningTotalCol.toLowerCase().includes('aprobado') || runningTotalCol.toLowerCase().includes('contratado');
                return {
                    ...row,
                    'Suma Acumulada': isCurrency ? formatCLP(cumulative) : cumulative.toLocaleString('es-CL')
                };
            });
        }

        // 4. Promedio Móvil (Moving Average)
        if (movingAverageCol) {
            resultRows = resultRows.map((row, idx) => {
                let sum = 0;
                let count = 0;
                const start = Math.max(0, idx - movingAveragePeriod + 1);
                for (let i = start; i <= idx; i++) {
                    const rawVal = String(resultRows[i][movingAverageCol] || '').replace(/[^\d.-]/g, '');
                    const num = parseFloat(rawVal);
                    if (!isNaN(num)) {
                        sum += num;
                        count++;
                    }
                }
                const avg = count > 0 ? sum / count : 0;
                const isCurrency = movingAverageCol.includes('$') || movingAverageCol.toLowerCase().includes('valor') || movingAverageCol.toLowerCase().includes('monto') || movingAverageCol.toLowerCase().includes('aprobado') || movingAverageCol.toLowerCase().includes('contratado');
                return {
                    ...row,
                    [`Prom. Móvil (N=${movingAveragePeriod})`]: isCurrency ? formatCLP(avg) : `${avg.toFixed(1)}%`
                };
            });
        }

        // 5. Variación de Avance (Delta %)
        if (deltaCol) {
            resultRows = resultRows.map((row, idx) => {
                let delta = 0;
                const rawValCur = String(row[deltaCol] || '').replace(/[^\d.-]/g, '');
                const numCur = parseFloat(rawValCur);
                
                if (idx > 0 && !isNaN(numCur)) {
                    const rawValPrev = String(resultRows[idx - 1][deltaCol] || '').replace(/[^\d.-]/g, '');
                    const numPrev = parseFloat(rawValPrev);
                    if (!isNaN(numPrev) && numPrev !== 0) {
                        delta = ((numCur - numPrev) / numPrev) * 100;
                    } else if (numPrev === 0 && numCur > 0) {
                        delta = 100;
                    }
                }
                return {
                    ...row,
                    'Variación Delta %': `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}%`
                };
            });
        }

        return resultRows;
    };

    // --- RENDERIZADOR DE GRÁFICOS DEL WIDGET ---
    const renderWidgetChart = (widget: any, rows: any[], cols: string[]) => {
        if (rows.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center p-6 text-center text-slate-400 dark:text-slate-500 italic">
                    Sin registros para graficar.
                </div>
            );
        }

        const xKey = widget.xAxisKey || cols[0] || 'name';
        const yKey = widget.yAxisKey || cols[cols.length - 1] || 'value';

        if (widget.type === 'bar-vertical') {
            const chartData = rows.map(r => {
                const rawVal = String(r[yKey] || '').replace(/[^\d.-]/g, '');
                const num = parseFloat(rawVal);
                return {
                    name: String(r[xKey] || '').substring(0, 15),
                    value: isNaN(num) ? 0 : num
                };
            }).slice(0, 10);

            return (
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis dataKey="name" tick={{ fontSize: 8 }} stroke="#888888" />
                        <YAxis tick={{ fontSize: 8 }} stroke="#888888" />
                        <Tooltip wrapperStyle={{ fontSize: 10 }} />
                        <Bar dataKey="value" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        if (widget.type === 'bar-horizontal') {
            const chartData = rows.map(r => {
                const rawVal = String(r[yKey] || '').replace(/[^\d.-]/g, '');
                const num = parseFloat(rawVal);
                return {
                    name: String(r[xKey] || '').substring(0, 15),
                    value: isNaN(num) ? 0 : num
                };
            }).slice(0, 10);

            return (
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis type="number" tick={{ fontSize: 8 }} stroke="#888888" />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 8 }} stroke="#888888" width={60} />
                        <Tooltip wrapperStyle={{ fontSize: 10 }} />
                        <Bar dataKey="value" fill="#3b82f6" radius={[0, 3, 3, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            );
        }

        if (widget.type === 'pie') {
            const counts: Record<string, number> = {};
            rows.forEach(r => {
                const key = String(r[xKey] || 'Otros');
                const rawVal = yKey ? String(r[yKey] || '').replace(/[^\d.-]/g, '') : '';
                const num = parseFloat(rawVal);
                counts[key] = (counts[key] || 0) + (isNaN(num) || !yKey ? 1 : num);
            });

            const chartData = Object.entries(counts).map(([name, value], i) => ({
                name: name.substring(0, 15),
                value,
                color: `hsl(${(i * 137.5) % 360}, 65%, 55%)`
            })).slice(0, 8);

            return (
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={65}
                            paddingAngle={2}
                            dataKey="value"
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Tooltip wrapperStyle={{ fontSize: 10 }} />
                        <Legend verticalAlign="bottom" height={24} wrapperStyle={{ fontSize: '8px' }} />
                    </PieChart>
                </ResponsiveContainer>
            );
        }

        if (widget.type === 'matrix') {
            const grouped: Record<string, any[]> = {};
            rows.forEach(r => {
                const key = String(r[xKey] || 'Otros');
                if (!grouped[key]) grouped[key] = [];
                grouped[key].push(r);
            });

            return (
                <div className="flex-1 overflow-y-auto space-y-1.5 text-[10px] font-sans max-h-[260px] pr-1">
                    {Object.keys(grouped).map(groupKey => {
                        const groupRows = grouped[groupKey];
                        return (
                            <details key={groupKey} className="group border border-slate-100 dark:border-slate-700/60 rounded-xl bg-slate-50/30 dark:bg-slate-900/10">
                                <summary className="flex items-center gap-1.5 p-2 hover:bg-slate-50 dark:hover:bg-slate-800/80 font-black cursor-pointer text-slate-800 dark:text-slate-200 select-none">
                                    <span className="material-symbols-outlined text-[14px] text-slate-400 group-open:rotate-90 transition-transform">keyboard_arrow_right</span>
                                    <span className="truncate">{groupKey}</span>
                                    <span className="ml-auto text-[8px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                                        {groupRows.length} {groupRows.length === 1 ? 'reg' : 'regs'}
                                    </span>
                                </summary>
                                <div className="p-2 border-t border-slate-100 dark:border-slate-700/30 bg-white dark:bg-slate-800/20 space-y-1.5 ml-3">
                                    {groupRows.slice(0, 10).map((r, idx) => (
                                        <div key={idx} className="p-1.5 border-b border-slate-100 dark:border-slate-700/20 text-[9px] space-y-1">
                                            {cols.filter(c => c !== xKey).slice(0, 4).map(col => (
                                                <div key={col} className="flex justify-between">
                                                    <span className="text-slate-400 font-medium">{col}:</span>
                                                    <span className="text-slate-700 dark:text-slate-355 font-bold">{r[col] !== undefined ? String(r[col]) : '-'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </details>
                        );
                    })}
                </div>
            );
        }

        return null;
    };

    // --- RENDERIZADOR DE TABLA SPREADSHEET (EXCEL INDEPENDIENTE) ---
    const renderExcelSpreadsheet = (rows: any[], columns: string[]) => {
        if (rows.length === 0) {
            return (
                <div className="p-8 text-center text-xs text-slate-450 dark:text-slate-500 italic bg-slate-50/50 dark:bg-slate-900/10 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 py-12">
                    No hay registros de datos para mostrar.
                </div>
            );
        }

        return (
            <div className="w-full overflow-x-auto rounded-2xl border border-slate-200/60 dark:border-slate-700/60 bg-white/50 dark:bg-slate-900/40 shadow-sm max-h-[400px] overflow-y-auto">
                <table className="w-full border-collapse text-left text-xs">
                    <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 border-b border-slate-200/60 dark:border-slate-700/60 z-10">
                        <tr>
                            {columns.map((col, idx) => (
                                <th key={idx} className="px-4 py-3 font-extrabold uppercase tracking-wider text-[10px] text-slate-400 dark:text-slate-500">
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50 text-slate-700 dark:text-slate-200">
                        {rows.map((row, rowIdx) => (
                            <tr key={rowIdx} className="hover:bg-slate-50/40 dark:hover:bg-slate-800/20 transition-colors">
                                {columns.map((col, colIdx) => {
                                    const val = row[col];
                                    
                                    // Evaluar formato condicional para esta celda
                                    let cellStyle = "";
                                    const matchingRule = cfRules.find(rule => {
                                        if (rule.column !== col) return false;
                                        const cellValStr = String(val || '').toLowerCase();
                                        const ruleValStr = String(rule.value).toLowerCase();

                                        if (rule.operator === 'less') {
                                            const cellNum = parseFloat(cellValStr.replace(/[^\d.-]/g, ''));
                                            const ruleNum = parseFloat(ruleValStr);
                                            return !isNaN(cellNum) && !isNaN(ruleNum) && cellNum < ruleNum;
                                        }
                                        if (rule.operator === 'greater') {
                                            const cellNum = parseFloat(cellValStr.replace(/[^\d.-]/g, ''));
                                            const ruleNum = parseFloat(ruleValStr);
                                            return !isNaN(cellNum) && !isNaN(ruleNum) && cellNum > ruleNum;
                                        }
                                        if (rule.operator === 'equal') {
                                            return cellValStr === ruleValStr;
                                        }
                                        if (rule.operator === 'contains') {
                                            return cellValStr.includes(ruleValStr);
                                        }
                                        return false;
                                    });

                                    if (matchingRule) {
                                        if (matchingRule.color === 'red') cellStyle = "bg-red-100 dark:bg-red-955/40 text-red-800 dark:text-red-300 font-bold";
                                        if (matchingRule.color === 'green') cellStyle = "bg-green-100 dark:bg-green-955/40 text-green-800 dark:text-green-300 font-bold";
                                        if (matchingRule.color === 'blue') cellStyle = "bg-blue-100 dark:bg-blue-955/40 text-blue-800 dark:text-blue-300 font-bold";
                                        if (matchingRule.color === 'yellow') cellStyle = "bg-yellow-100 dark:bg-yellow-955/40 text-yellow-800 dark:text-yellow-300 font-bold";
                                    }

                                    return (
                                        <td key={colIdx} className={`px-4 py-2.5 whitespace-nowrap transition-colors ${cellStyle}`}>
                                            {val !== undefined ? String(val) : '-'}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const hasAccess = isAdmin || canView || canDesign;
    if (!hasAccess) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm min-h-[500px]">
                <div className="size-16 rounded-2xl bg-red-500/10 text-red-500 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
                    <span className="material-symbols-outlined text-[36px]">security</span>
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Acceso Denegado</h3>
                <p className="text-xs text-slate-600 dark:text-slate-400 max-w-sm mx-auto">
                    No cuenta con los permisos necesarios para visualizar o diseñar reportes. Contacte a un administrador.
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full bg-slate-50/90 dark:bg-slate-900/90 rounded-2xl overflow-hidden shadow-sm transition-all glow-blue">
            {config.isMocked && (
                <style dangerouslySetInnerHTML={{ __html: `
                    @media print {
                        body * {
                            visibility: hidden;
                        }
                        #print-local-report-content, #print-local-report-content * {
                            visibility: visible;
                        }
                        #print-local-report-content {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100% !important;
                            height: auto !important;
                            background: white !important;
                            color: black !important;
                        }
                        .recharts-responsive-container {
                            width: 100% !important;
                            height: 320px !important;
                            display: block !important;
                        }
                        .recharts-wrapper {
                            width: 100% !important;
                            height: 320px !important;
                        }
                        svg {
                            width: 100% !important;
                            height: 100% !important;
                            display: block !important;
                        }
                        .no-print {
                            display: none !important;
                        }
                    }
                ` }} />
            )}

            {/* Header Control Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-2.5 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 no-print shrink-0">
                <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 text-[9px] font-black tracking-wider uppercase rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-800/60">
                        Capa SQL bi Activa
                    </span>
                    {config.isMocked && (
                        <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">
                            Reporte local interactivo
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {/* View/Edit Mode Switcher */}
                    {(isAdmin || canDesign || canModifyDesign) && (
                        <div className="flex bg-slate-100 dark:bg-slate-900 p-0.5 rounded-lg border border-slate-200/60 dark:border-slate-700/60">
                            <button
                                onClick={() => setMode('view')}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-wider transition-all ${mode === 'view'
                                        ? 'bg-white dark:bg-slate-800 text-primary shadow-sm'
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-[14px]">visibility</span>
                                Lectura
                            </button>
                            <button
                                onClick={() => setMode('edit')}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-wider transition-all ${mode === 'edit'
                                        ? 'bg-white dark:bg-slate-800 text-amber-600 dark:text-amber-400 shadow-sm'
                                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-[14px]">edit</span>
                                Edición
                            </button>
                        </div>
                    )}

                    <button
                        onClick={() => setShowDiagPanel(!showDiagPanel)}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-extrabold uppercase tracking-wider border transition-all ${showDiagPanel
                                ? 'bg-primary text-white border-primary shadow-md'
                                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                        title="Ver diagnóstico de conexión y variables"
                    >
                        <span className="material-symbols-outlined text-[14px]">settings_ethernet</span>
                        Diagnóstico BD
                    </button>
                </div>
            </div>

            {/* Diagnostic Drawer */}
            {showDiagPanel && (
                <div className="bg-slate-100 dark:bg-slate-950/80 border-b border-slate-200 dark:border-slate-800 p-5 space-y-4 animate-fade-in no-print">
                    {config.status && (
                        <div className="px-4 py-2.5 rounded-xl text-xs bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-900 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[18px]">info</span>
                            <span><strong>Estado de la API:</strong> {config.status}</span>
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                        <div className="bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                            <span className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Esquema Analítico SQL</span>
                            <p className="text-emerald-600 dark:text-emerald-400 font-mono font-medium">{status.sqlSchemaStatus}</p>
                            <p className="text-[11px] text-slate-500 mt-1">7 vistas analíticas + Rol NOLOGIN bi_reader</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                            <span className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Métricas PPC Reconciliadas</span>
                            <p className="text-primary font-bold">PPC_VALID: {ppcMetrics.ppcValid.toFixed(2)}%</p>
                            <p className="text-slate-500 dark:text-slate-400">PPC_GROSS: {ppcMetrics.ppcGross.toFixed(2)}%</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                            <span className="font-bold text-slate-700 dark:text-slate-300 block mb-1">Parámetros Reporte</span>
                            <p className="text-slate-600 dark:text-slate-400 font-mono text-[11px]">
                                ID: {config.reportId || reportId || 'No cargado'}
                            </p>
                            <p className="text-slate-600 dark:text-slate-400 font-mono text-[11px]">
                                Tipo de Render: Renderizador Local (HTML5/Canvas)
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Render Canvas */}
            <div className="flex-1 relative min-h-[600px] bg-slate-100 dark:bg-slate-950 flex flex-col">
                {loading ? (
                    <div className="flex flex-col items-center justify-center flex-1 gap-3 p-8">
                        <div className="size-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-xs font-medium text-slate-600 dark:text-slate-400">
                            Cargando componentes analíticos...
                        </p>
                    </div>
                ) : error ? (
                    <div className="max-w-xl text-center p-8 space-y-5 animate-fade-in mx-auto mt-16">
                        <div className="size-16 rounded-2xl bg-red-500/10 text-red-500 border border-red-500/20 flex items-center justify-center mx-auto shadow-inner">
                            <span className="material-symbols-outlined text-[36px]">report_problem</span>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                Error de Conexión Analítica
                            </h3>
                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed max-w-sm mx-auto">
                                {error.message}
                            </p>
                        </div>

                        <div className="bg-slate-900 text-slate-200 text-left p-4 rounded-xl font-mono text-[11px] space-y-1 shadow-inner border border-slate-800 max-w-sm mx-auto">
                            <p><span className="text-red-400">Código:</span> {error.code}</p>
                            {error.requestId && <p><span className="text-amber-400">Solicitud ID:</span> {error.requestId}</p>}
                        </div>
                    </div>
                ) : showPermissionError ? (
                    <div className="max-w-xl text-center p-8 space-y-4 animate-fade-in mx-auto mt-16">
                        <div className="size-16 rounded-2xl bg-red-500/10 text-red-500 border border-red-500/20 flex items-center justify-center mx-auto shadow-inner">
                            <span className="material-symbols-outlined text-[36px]">security</span>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                Modo Edición No Autorizado por el Servidor
                            </h3>
                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                La sesión no cuenta con privilegios de edición.
                            </p>
                        </div>
                    </div>
                ) : config.isMocked && (action === 'create' || mode === 'edit') ? (
                    // ==========================================
                    // LOCAL DESIGNER / BUILDER CANVAS
                    // ==========================================
                    <div className="flex flex-1 overflow-hidden h-full">
                        {/* Columna 1 (Izquierda): Propiedades + Componentes */}
                        <div className="w-80 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 p-5 flex flex-col justify-between h-full shrink-0">
                            <div className="flex-1 overflow-y-auto pr-1 mb-4 space-y-4">
                                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Propiedades del Reporte</h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-[10px] font-bold text-slate-605 dark:text-slate-400 block mb-1">Nombre del Reporte</label>
                                        <input 
                                            type="text" 
                                            value={reportTitle} 
                                            onChange={e => setReportTitle(e.target.value)} 
                                            className="w-full text-xs p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 focus:outline-none focus:border-primary text-slate-800 dark:text-white"
                                            placeholder="Ej: Reporte de Avance"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold text-slate-605 dark:text-slate-400 block mb-1">Alcance (Filtro RLS)</label>
                                        <select 
                                            value={reportScope} 
                                            onChange={e => setReportScope(e.target.value as any)}
                                            className="w-full text-xs p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white cursor-pointer"
                                        >
                                            <option value="obra">Obra (Solo este proyecto)</option>
                                            <option value="multiobra">Multiobra (Proyectos asignados)</option>
                                            <option value="corporativo">Corporativo (Todos los proyectos)</option>
                                        </select>
                                        {reportScope === 'multiobra' && projectsList.length > 0 && (
                                            <div className="space-y-1.5 border border-slate-200 dark:border-slate-700 p-3 rounded-xl bg-slate-50 dark:bg-slate-900 max-h-36 overflow-y-auto mt-2">
                                                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Obras autorizadas:</label>
                                                {projectsList.map(proj => (
                                                    <label key={proj.project_id} className="flex items-center gap-1.5 p-1 hover:bg-slate-100/50 dark:hover:bg-slate-800/40 cursor-pointer select-none">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedProjects.includes(proj.project_id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedProjects(prev => [...prev, proj.project_id]);
                                                                } else {
                                                                    setSelectedProjects(prev => prev.filter(id => id !== proj.project_id));
                                                                }
                                                            }}
                                                            className="rounded text-primary h-3 w-3"
                                                        />
                                                        <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300">{proj.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold text-slate-605 dark:text-slate-400 block mb-1">Módulos</label>
                                        <div className="grid grid-cols-2 gap-2 bg-slate-50 dark:bg-slate-900 p-2.5 rounded-xl border border-slate-200/60 dark:border-slate-700/60">
                                            {['Planificación', 'Calidad', 'Subcontratos'].map(mod => {
                                                const isChecked = reportCategories.includes(mod);
                                                return (
                                                    <label key={mod} className="flex items-center gap-1.5 text-[10px] font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={isChecked} 
                                                            onChange={() => {
                                                                setReportCategories(prev => {
                                                                    const updated = prev.includes(mod) ? prev.filter(x => x !== mod) : [...prev, mod];
                                                                    setSelectedComponents(compPrev => {
                                                                        let newComps = [...compPrev];
                                                                        if (prev.includes(mod)) {
                                                                            if (mod === 'Planificación') newComps = newComps.filter(c => !['scurve', 'intermediate', 'ppc'].includes(c));
                                                                            if (mod === 'Calidad') newComps = newComps.filter(c => c !== 'quality');
                                                                            if (mod === 'Subcontratos') newComps = newComps.filter(c => c !== 'subcontracts');
                                                                        } else {
                                                                            if (mod === 'Planificación') newComps = [...new Set([...newComps, 'scurve', 'intermediate', 'ppc'])];
                                                                            if (mod === 'Calidad') newComps = [...new Set([...newComps, 'quality'])];
                                                                            if (mod === 'Subcontratos') newComps = [...new Set([...newComps, 'subcontracts'])];
                                                                        }
                                                                        if (!newComps.includes('kpi')) {
                                                                            newComps.unshift('kpi');
                                                                        }
                                                                        if (newComps.length > 0 && !newComps.includes(activeEditingComponent)) {
                                                                            setActiveEditingComponent(newComps[0]);
                                                                        } else if (newComps.length === 0) {
                                                                            setActiveEditingComponent('kpi');
                                                                        }
                                                                        return newComps;
                                                                    });
                                                                    return updated;
                                                                 });
                                                            }}
                                                            className="rounded text-primary h-3 w-3"
                                                        />
                                                        {mod}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold text-slate-605 dark:text-slate-400 block mb-1">Descripción</label>
                                        <textarea 
                                            value={reportDescription} 
                                            onChange={e => setReportDescription(e.target.value)}
                                            rows={2}
                                            className="w-full text-xs p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 focus:outline-none focus:border-primary text-slate-800 dark:text-white resize-none"
                                            placeholder="Descripción del reporte..."
                                        />
                                    </div>
                                </div>

                                <div className="border-t border-slate-150 dark:border-slate-700/50 pt-3">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Componentes del Reporte</h3>
                                    <div className="space-y-2">
                                        {[
                                            { id: 'kpi', label: 'Resumen Ejecutivo (KPIs)', desc: 'Métricas de resumen consolidado.', module: 'Siempre' },
                                            { id: 'scurve', label: 'Planificación Maestra (Gantt)', desc: 'Curva S de avances reales.', module: 'Planificación' },
                                            { id: 'intermediate', label: 'Planificación Intermedia', desc: 'Restricciones de obra.', module: 'Planificación' },
                                            { id: 'ppc', label: 'Planificación Semanal (LPS)', desc: 'PPC de compromisos.', module: 'Planificación' },
                                            { id: 'quality', label: 'Control de Calidad', desc: 'Protocolos de terreno.', module: 'Calidad' },
                                            { id: 'subcontracts', label: 'Subcontrato', desc: 'Importes y avances EP.', module: 'Subcontratos' }
                                        ].filter(c => c.module === 'Siempre' || reportCategories.includes(c.module)).map(c => {
                                            const isChecked = selectedComponents.includes(c.id);
                                            const isActive = activeEditingComponent === c.id;
                                            return (
                                                <div key={c.id} className={`rounded-xl border transition-all ${isActive ? 'border-primary bg-primary/5' : 'border-slate-205 dark:border-slate-700 bg-slate-50/20 dark:bg-slate-800/10'}`}>
                                                    <div className="flex items-center justify-between p-2">
                                                        <label className="flex items-center gap-2 cursor-pointer flex-1 select-none">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={isChecked} 
                                                                onChange={() => toggleComponent(c.id)}
                                                                className="rounded text-primary focus:ring-primary h-3.5 w-3.5"
                                                            />
                                                            <div className="truncate">
                                                                <span className="text-[11px] font-bold text-slate-880 dark:text-slate-200 block truncate">{c.label}</span>
                                                            </div>
                                                        </label>
                                                        {isChecked && (
                                                            <button 
                                                                onClick={() => setActiveEditingComponent(c.id)}
                                                                className={`p-1 rounded-lg text-[16px] material-symbols-outlined hover:bg-slate-200 dark:hover:bg-slate-750 ${isActive ? 'text-primary font-bold' : 'text-slate-400'}`}
                                                                title="Configurar visualización"
                                                            >
                                                                settings
                                                            </button>
                                                        )}
                                                    </div>
                                                    {isChecked && isActive && DEFAULT_COLUMNS[c.id] && (
                                                        <div className="px-2 pb-2 pt-1 border-t border-slate-100 dark:border-slate-700/50 bg-white dark:bg-slate-800/50 space-y-1 max-h-36 overflow-y-auto">
                                                            <span className="text-[8px] font-black uppercase text-slate-400 tracking-wider block">Columnas Visibles</span>
                                                            {DEFAULT_COLUMNS[c.id].map(col => {
                                                                const isColChecked = (selectedColumns[c.id] || []).includes(col);
                                                                return (
                                                                    <label key={col} className="flex items-center gap-1.5 text-[9px] text-slate-650 dark:text-slate-350 hover:text-primary cursor-pointer select-none">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isColChecked}
                                                                            onChange={() => {
                                                                                setSelectedColumns(prev => {
                                                                                    const current = prev[c.id] || [];
                                                                                    const updated = current.includes(col)
                                                                                        ? current.filter(x => x !== col)
                                                                                        : [...current, col];
                                                                                    return { ...prev, [c.id]: updated };
                                                                                });
                                                                            }}
                                                                            className="rounded text-primary h-2.5 w-2.5"
                                                                        />
                                                                        <span className="truncate">{col}</span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2 border-t border-slate-150 dark:border-slate-700/50 pt-3 bg-white dark:bg-slate-800">
                                <button
                                    onClick={handleSaveLocalReport}
                                    disabled={savingLocal}
                                    className="w-full py-2.5 px-4 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primary/95 transition-all shadow-md shadow-primary/20 flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    <span className="material-symbols-outlined text-[18px]">save</span>
                                    {savingLocal ? 'Guardando...' : 'Guardar Reporte'}
                                </button>
                            </div>
                        </div>

                        {/* Columna 2 (Centro): Canvas Live Preview (Grilla Multigráficos + Spreadsheet) */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6 max-h-[calc(100vh-12rem)] flex flex-col bg-slate-105 dark:bg-slate-950">

                            {/* Panel Multigráfico del Canvas */}
                            <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm space-y-4 shrink-0">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-xs font-black uppercase text-slate-400 tracking-wider">Panel de Gráficos del Reporte</h4>
                                    <span className="text-[10px] bg-primary/10 text-primary px-2.5 py-1 rounded-full font-bold">
                                        {widgets.length} {widgets.length === 1 ? 'Gráfico' : 'Gráficos'} activos
                                    </span>
                                </div>

                                {widgets.length === 0 ? (
                                    <div className="p-8 text-center text-xs text-slate-400 dark:text-slate-500 italic bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 py-12">
                                        <span className="material-symbols-outlined text-[32px] block mb-2 text-slate-350">monitoring</span>
                                        Aún no has añadido gráficos a este reporte.<br />
                                        Selecciona un tipo de gráfico en la barra derecha, configura sus ejes y haz clic en "Añadir Gráfico".
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-5 w-full">
                                        {widgets.map((widget) => {
                                            // Obtener datos del componente correspondiente al widget
                                            let widgetSourceRows: any[] = [];
                                            if (widget.componentId === 'kpi') widgetSourceRows = kpiRows;
                                            else if (widget.componentId === 'scurve') widgetSourceRows = scurveRows;
                                            else if (widget.componentId === 'intermediate') widgetSourceRows = intermediateRows;
                                            else if (widget.componentId === 'ppc') widgetSourceRows = ppcRows;
                                            else if (widget.componentId === 'quality') widgetSourceRows = qualityRows;
                                            else if (widget.componentId === 'subcontracts') widgetSourceRows = subcontractRows;

                                            // Procesar cálculos aplicados
                                            const widgetRows = processCalculations(widget.componentId, widgetSourceRows);
                                            const widgetCols = selectedColumns[widget.componentId] || DEFAULT_COLUMNS[widget.componentId] || [];

                                            // Ancho de la grilla
                                            let gridWidth = 'w-full';
                                            if (widget.width === '1/2') gridWidth = 'w-full md:w-[calc(50%-10px)]';
                                            if (widget.width === '1/3') gridWidth = 'w-full md:w-[calc(33.3%-14px)]';

                                            return (
                                                <div 
                                                    key={widget.id} 
                                                    className={`${gridWidth} backdrop-blur-md bg-white/70 dark:bg-slate-900/70 p-5 rounded-2xl border border-white/20 dark:border-slate-800 shadow-md flex flex-col min-h-[360px] relative transition-all hover:shadow-lg`}
                                                >
                                                    {/* Cabecera del Widget */}
                                                    <div className="flex items-center justify-between mb-4 border-b border-slate-100 dark:border-slate-800/80 pb-2">
                                                        <div className="truncate pr-4">
                                                            <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block">
                                                                {widget.componentId === 'kpi' && 'Resumen Ejecutivo (KPIs)'}
                                                                {widget.componentId === 'scurve' && 'Planificación Gantt'}
                                                                {widget.componentId === 'intermediate' && 'Restricciones'}
                                                                {widget.componentId === 'ppc' && 'PPC Semanal'}
                                                                {widget.componentId === 'quality' && 'Control Calidad'}
                                                                {widget.componentId === 'subcontracts' && 'Subcontratos'}
                                                            </span>
                                                            <h5 className="text-[11px] font-extrabold text-slate-880 dark:text-slate-205 truncate">
                                                                {widget.type === 'bar-vertical' && 'Barras V'}
                                                                {widget.type === 'bar-horizontal' && 'Barras H'}
                                                                {widget.type === 'pie' && 'Gráfico Circular'}
                                                                {widget.type === 'matrix' && 'Matriz Jerárquica'}
                                                                {` por ${widget.xAxisKey || 'General'}`}
                                                            </h5>
                                                        </div>

                                                        {/* Controles de Redimensión, Edición y Borrado */}
                                                        <div className="flex items-center gap-1 shrink-0">
                                                            {/* Selector de tamaño */}
                                                            {['1/3', '1/2', 'full'].map(sz => (
                                                                <button
                                                                    key={sz}
                                                                    onClick={() => setWidgets(prev => prev.map(w => w.id === widget.id ? { ...w, width: sz } : w))}
                                                                    className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase transition-colors ${widget.width === sz ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-850 text-slate-400 hover:bg-slate-200'}`}
                                                                    title={`Ajustar a ${sz}`}
                                                                >
                                                                    {sz}
                                                                </button>
                                                            ))}
                                                            
                                                            {/* Botón eliminar */}
                                                            <button
                                                                onClick={() => setWidgets(prev => prev.filter(w => w.id !== widget.id))}
                                                                className="p-1 hover:bg-red-50 dark:hover:bg-red-950/20 text-red-500 rounded-lg material-symbols-outlined text-[16px] ml-1"
                                                                title="Quitar Gráfico"
                                                            >
                                                                delete
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Área del Gráfico */}
                                                    <div className="flex-1 w-full min-h-[260px] flex flex-col justify-center">
                                                        {renderWidgetChart(widget, widgetRows, widgetCols)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Hoja de Datos y Transformaciones Analíticas (Excel independiente) */}
                            <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col min-h-[380px] shrink-0">
                                {activeEditingComponent ? (() => {
                                    let sourceRows: any[] = [];
                                    if (activeEditingComponent === 'scurve') sourceRows = scurveRows;
                                    else if (activeEditingComponent === 'intermediate') sourceRows = intermediateRows;
                                    else if (activeEditingComponent === 'ppc') sourceRows = ppcRows;
                                    else if (activeEditingComponent === 'quality') sourceRows = qualityRows;
                                    else if (activeEditingComponent === 'subcontracts') sourceRows = subcontractRows;

                                    const processedSpreadsheetRows = processCalculations(activeEditingComponent, sourceRows);
                                    let activeColumns = selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || [];
                                    
                                    // Añadir columnas virtuales reactivas si están configuradas
                                    if (concatColA && concatColB && !activeColumns.includes('Concatenado')) {
                                        activeColumns = [...activeColumns, 'Concatenado'];
                                    }
                                    if (runningTotalCol && !activeColumns.includes('Suma Acumulada')) {
                                        activeColumns = [...activeColumns, 'Suma Acumulada'];
                                    }
                                    if (movingAverageCol && !activeColumns.includes(`Prom. Móvil (N=${movingAveragePeriod})`)) {
                                        activeColumns = [...activeColumns, `Prom. Móvil (N=${movingAveragePeriod})`];
                                    }
                                    if (deltaCol && !activeColumns.includes('Variación Delta %')) {
                                        activeColumns = [...activeColumns, 'Variación Delta %'];
                                    }

                                    return (
                                        <>
                                            <div className="flex items-center justify-between mb-4 border-b border-slate-100 dark:border-slate-750 pb-2">
                                                <div>
                                                    <h4 className="text-xs font-black uppercase text-slate-800 dark:text-slate-250">
                                                        Hoja de Cálculo K-LyDApp (Spreadsheet Live)
                                                    </h4>
                                                    <p className="text-[9px] text-slate-400 mt-0.5">
                                                        Visualización interactiva independiente con transformaciones lógicas y matemáticas en tiempo real del componente {activeEditingComponent.toUpperCase()}.
                                                    </p>
                                                </div>
                                                <span className="text-[10px] font-bold text-slate-450">
                                                    {processedSpreadsheetRows.length} registros filtrados
                                                </span>
                                            </div>

                                            {/* Renderizar la tabla Excel con Formato Condicional */}
                                            {renderExcelSpreadsheet(processedSpreadsheetRows, activeColumns)}
                                        </>
                                    );
                                })() : (
                                    <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 py-12">
                                        <span className="material-symbols-outlined text-[32px] block mb-2 text-slate-350">table_chart</span>
                                        Selecciona un componente de la izquierda para desplegar y modelar su Hoja de Cálculo Excel asociada.
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Columna 3 (Derecha): Visualizaciones y Capa DAX */}
                        <div className="w-80 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 p-5 flex flex-col h-full shrink-0">
                            {activeEditingComponent ? (
                                <div className="flex-1 overflow-y-auto space-y-5 pr-1 text-xs">
                                    <div>
                                        <span className="text-[10px] font-bold text-primary uppercase tracking-widest block mb-0.5">Componente Activo</span>
                                        <h3 className="text-xs font-black text-slate-850 dark:text-white uppercase">
                                            {activeEditingComponent === 'kpi' && 'Resumen Ejecutivo'}
                                            {activeEditingComponent === 'scurve' && 'Planificación Maestra (Gantt)'}
                                            {activeEditingComponent === 'intermediate' && 'Planificación Intermedia'}
                                            {activeEditingComponent === 'ppc' && 'Planificación Semanal (LPS)'}
                                            {activeEditingComponent === 'quality' && 'Control de Calidad'}
                                            {activeEditingComponent === 'subcontracts' && 'Subcontratos'}
                                        </h3>
                                    </div>

                                    {/* 1. SECCIÓN GRÁFICOS */}
                                    <div className="space-y-3 border-t border-slate-105 dark:border-slate-700/50 pt-3">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Gráficos disponibles</span>
                                        
                                        {/* Grid de iconos (Se iluminan en azul al seleccionarse) */}
                                        <div className="grid grid-cols-4 gap-2 bg-slate-50 dark:bg-slate-900/60 p-2 rounded-xl border border-slate-200/60 dark:border-slate-700/60">
                                            {[
                                                { id: 'bar-vertical', icon: 'bar_chart', label: 'Barras V' },
                                                { id: 'bar-horizontal', icon: 'notes', label: 'Barras H' },
                                                { id: 'pie', icon: 'pie_chart', label: 'Torta' },
                                                { id: 'matrix', icon: 'schema', label: 'Matriz' }
                                            ].map(vis => {
                                                const isSelected = selectedVisualType === vis.id;
                                                return (
                                                    <button
                                                        key={vis.id}
                                                        onClick={() => {
                                                            setSelectedVisualType(prev => prev === vis.id ? null : (vis.id as any));
                                                            // Asignar llaves de ejes por defecto al cambiar visual
                                                            const activeCols = selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || [];
                                                            if (activeCols.length > 0) {
                                                                setTempXAxisKey(activeCols[0]);
                                                                setTempYAxisKey(activeCols[activeCols.length - 1]);
                                                            }
                                                        }}
                                                        className={`flex flex-col items-center justify-center p-2.5 rounded-lg border transition-all ${isSelected ? 'border-primary bg-primary text-white shadow-sm shadow-primary/20 scale-[1.03]' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                                                        title={vis.label}
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">{vis.icon}</span>
                                                        <span className="text-[8px] font-bold mt-1 tracking-tighter">{vis.label}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* Subpanel de Configuración del Widget a insertar */}
                                        {selectedVisualType && (
                                            <div className="p-3 bg-slate-50 dark:bg-slate-900/40 rounded-xl border border-slate-105 dark:border-slate-800 space-y-3 animate-fade-in">
                                                <span className="text-[9px] font-black uppercase text-primary tracking-widest block">Insertar nuevo gráfico</span>
                                                
                                                {/* Eje X */}
                                                <div className="space-y-1">
                                                    <span className="text-[9px] font-bold text-slate-505 block">Eje X (Agrupación)</span>
                                                    <select
                                                        value={tempXAxisKey}
                                                        onChange={e => setTempXAxisKey(e.target.value)}
                                                        className="w-full p-2 bg-white dark:bg-slate-800 border rounded-lg text-xs"
                                                    >
                                                        {(selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || []).map(col => (
                                                            <option key={col} value={col}>{col}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Eje Y */}
                                                {selectedVisualType !== 'matrix' && (
                                                    <div className="space-y-1">
                                                        <span className="text-[9px] font-bold text-slate-505 block">Eje Y (Valores)</span>
                                                        <select
                                                            value={tempYAxisKey}
                                                            onChange={e => setTempYAxisKey(e.target.value)}
                                                            className="w-full p-2 bg-white dark:bg-slate-800 border rounded-lg text-xs"
                                                        >
                                                            {(selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || []).map(col => (
                                                                <option key={col} value={col}>{col}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}

                                                {/* Tamaño */}
                                                <div className="space-y-1">
                                                    <span className="text-[9px] font-bold text-slate-505 block">Ancho del Gráfico</span>
                                                    <div className="flex gap-1.5">
                                                        {(['1/3', '1/2', 'full'] as const).map(sz => (
                                                            <button
                                                                key={sz}
                                                                onClick={() => setTempWidth(sz)}
                                                                className={`flex-1 py-1 rounded-lg text-[9px] font-black uppercase transition-colors ${tempWidth === sz ? 'bg-primary text-white' : 'bg-white dark:bg-slate-800 border text-slate-400'}`}
                                                            >
                                                                {sz === '1/3' ? '1/3' : sz === '1/2' ? '1/2' : 'Completo'}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => {
                                                        const newWidget = {
                                                            id: `widget-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
                                                            type: selectedVisualType,
                                                            componentId: activeEditingComponent,
                                                            xAxisKey: tempXAxisKey,
                                                            yAxisKey: tempYAxisKey,
                                                            width: tempWidth
                                                        };
                                                        setWidgets(prev => [...prev, newWidget]);
                                                        setSelectedVisualType(null); // Apagar selección e iluminación
                                                    }}
                                                    className="w-full py-2 bg-primary text-white text-[10px] font-black uppercase tracking-wider rounded-xl shadow-md shadow-primary/10 hover:scale-[1.01] transition-transform"
                                                >
                                                    Insertar Gráfico al Lienzo
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* 2. CÁLCULOS Y FÓRMULAS SOBRE TABLA */}
                                    <div className="space-y-4 border-t border-slate-105 dark:border-slate-700/50 pt-3">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">Cálculos y Fórmulas del Reporte</span>
                                        
                                        {/* Suma Acumulada */}
                                        <div className="space-y-1">
                                            <span className="text-[9px] font-bold text-slate-505 block">Suma Acumulada (Running Total)</span>
                                            <select
                                                value={runningTotalCol}
                                                onChange={e => setRunningTotalCol(e.target.value)}
                                                className="w-full p-2 bg-slate-50 dark:bg-slate-900 border rounded-lg text-xs"
                                            >
                                                <option value="">Ninguna</option>
                                                {(selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || []).map(col => (
                                                    <option key={col} value={col}>{col}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Promedio Móvil */}
                                        <div className="space-y-1">
                                            <span className="text-[9px] font-bold text-slate-505 block">Promedio Móvil (Moving Average)</span>
                                            <div className="flex gap-2">
                                                <select
                                                    value={movingAverageCol}
                                                    onChange={e => setMovingAverageCol(e.target.value)}
                                                    className="flex-1 p-2 bg-slate-50 dark:bg-slate-900 border rounded-lg text-xs"
                                                >
                                                    <option value="">Ninguna</option>
                                                    {(selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || []).map(col => (
                                                        <option key={col} value={col}>{col}</option>
                                                    ))}
                                                </select>
                                                {movingAverageCol && (
                                                    <input
                                                        type="number"
                                                        min={2}
                                                        max={12}
                                                        value={movingAveragePeriod}
                                                        onChange={e => setMovingAveragePeriod(Math.max(2, parseInt(e.target.value) || 2))}
                                                        className="w-14 p-2 bg-slate-50 dark:bg-slate-900 border rounded-lg text-xs text-center"
                                                        title="Período N"
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Variación Delta */}
                                        <div className="space-y-1">
                                            <span className="text-[9px] font-bold text-slate-505 block">Variación Delta % (Diferencial)</span>
                                            <select
                                                value={deltaCol}
                                                onChange={e => setDeltaCol(e.target.value)}
                                                className="w-full p-2 bg-slate-50 dark:bg-slate-900 border rounded-lg text-xs"
                                            >
                                                <option value="">Ninguna</option>
                                                {(selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || []).map(col => (
                                                    <option key={col} value={col}>{col}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Concatenación con Delimitador */}
                                        <div className="space-y-1">
                                            <span className="text-[9px] font-bold text-slate-505 block">Concatenar Columnas</span>
                                            <div className="grid grid-cols-2 gap-1.5">
                                                <select
                                                    value={concatColA}
                                                    onChange={e => setConcatColA(e.target.value)}
                                                    className="p-1.5 bg-slate-50 dark:bg-slate-900 border rounded-lg text-[10px]"
                                                >
                                                    <option value="">Columna A</option>
                                                    {(selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || []).map(col => (
                                                        <option key={col} value={col}>{col}</option>
                                                    ))}
                                                </select>
                                                <select
                                                    value={concatColB}
                                                    onChange={e => setConcatColB(e.target.value)}
                                                    className="p-1.5 bg-slate-50 dark:bg-slate-900 border rounded-lg text-[10px]"
                                                >
                                                    <option value="">Columna B</option>
                                                    {(selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || []).map(col => (
                                                        <option key={col} value={col}>{col}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            {concatColA && concatColB && (
                                                <div className="mt-1 flex items-center gap-1.5">
                                                    <span className="text-[8px] text-slate-400">Separador:</span>
                                                    <input
                                                        type="text"
                                                        value={concatDelimiter}
                                                        onChange={e => setConcatDelimiter(e.target.value)}
                                                        className="p-1 bg-white dark:bg-slate-800 border rounded text-[10px] w-14 text-center font-mono"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* 3. FILTROS AVANZADOS MULTIPLES (AND/OR) */}
                                        <div className="space-y-2 border-t border-slate-100 dark:border-slate-700/50 pt-3">
                                            <div className="flex justify-between items-center">
                                                <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider">Filtros Lógicos Combinados</span>
                                                {filterRules.length > 1 && (
                                                    <select
                                                        value={filterJoinType}
                                                        onChange={e => setFilterJoinType(e.target.value as any)}
                                                        className="text-[9px] font-black p-0.5 border bg-white dark:bg-slate-850 rounded text-slate-800 dark:text-white"
                                                    >
                                                        <option value="AND">Unión Y</option>
                                                        <option value="OR">Unión O</option>
                                                    </select>
                                                )}
                                            </div>

                                            {/* Reglas de Filtros existentes */}
                                            {filterRules.length > 0 && (
                                                <div className="space-y-1 max-h-24 overflow-y-auto border bg-slate-55/20 dark:bg-slate-900/10 p-1.5 rounded-lg text-[10px]">
                                                    {filterRules.map((rule, idx) => (
                                                        <div key={idx} className="flex justify-between items-center p-1 bg-white dark:bg-slate-800 rounded border border-slate-100 dark:border-slate-700">
                                                            <span className="truncate max-w-[130px] font-medium">{rule.column}: {rule.operator === 'equals' ? '=' : rule.operator === 'contains' ? 'cont.' : rule.operator === 'greater' ? '>' : '<'} "{rule.value}"</span>
                                                            <button
                                                                onClick={() => setFilterRules(prev => prev.filter((_, i) => i !== idx))}
                                                                className="text-red-500 text-[12px] hover:underline font-bold"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Añadir Regla de Filtro */}
                                            <button
                                                onClick={() => {
                                                    const cols = selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || [];
                                                    if (cols.length > 0) {
                                                        const newRule = {
                                                            column: cols[0],
                                                            operator: 'contains',
                                                            value: ''
                                                        };
                                                        setFilterRules(prev => [...prev, newRule]);
                                                    }
                                                }}
                                                className="w-full text-center py-1.5 border border-dashed rounded-lg text-[9px] font-bold text-primary hover:bg-primary/5 transition-colors"
                                            >
                                                + Añadir Criterio de Filtro
                                            </button>

                                            {/* Configurar reglas de filtros que aún no tienen valor */}
                                            {filterRules.map((rule, idx) => {
                                                if (rule.value !== undefined) {
                                                    const cols = selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || [];
                                                    return (
                                                        <div key={idx} className="grid grid-cols-3 gap-1 p-1.5 bg-slate-50 dark:bg-slate-900 rounded border border-slate-100 dark:border-slate-800/80 text-[9px]">
                                                            <select
                                                                value={rule.column}
                                                                onChange={e => setFilterRules(prev => prev.map((r, i) => i === idx ? { ...r, column: e.target.value } : r))}
                                                                className="p-1 bg-white dark:bg-slate-850 border rounded text-[9px] text-slate-850 dark:text-white"
                                                            >
                                                                {cols.map(c => <option key={c} value={c}>{c}</option>)}
                                                            </select>
                                                            <select
                                                                value={rule.operator}
                                                                onChange={e => setFilterRules(prev => prev.map((r, i) => i === idx ? { ...r, operator: e.target.value } : r))}
                                                                className="p-1 bg-white dark:bg-slate-850 border rounded text-[9px] text-slate-850 dark:text-white"
                                                            >
                                                                <option value="contains">Contiene</option>
                                                                <option value="equals">Igual</option>
                                                                <option value="greater">Mayor</option>
                                                                <option value="less">Menor</option>
                                                            </select>
                                                            <input
                                                                type="text"
                                                                value={rule.value}
                                                                onChange={e => setFilterRules(prev => prev.map((r, i) => i === idx ? { ...r, value: e.target.value } : r))}
                                                                placeholder="Valor..."
                                                                className="p-1 bg-white dark:bg-slate-800 border rounded text-[9px] w-full"
                                                            />
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>

                                        {/* 4. FORMATO CONDICIONAL */}
                                        <div className="space-y-2 border-t border-slate-100 dark:border-slate-700/50 pt-3">
                                            <span className="text-[9px] font-black uppercase text-slate-400 tracking-wider block">Formato Condicional (Semáforo)</span>
                                            
                                            {/* Reglas de Formato Condicional existentes */}
                                            {cfRules.length > 0 && (
                                                <div className="space-y-1 max-h-24 overflow-y-auto border bg-slate-55/20 dark:bg-slate-900/10 p-1.5 rounded-lg text-[10px]">
                                                    {cfRules.map((rule, idx) => (
                                                        <div key={idx} className="flex justify-between items-center p-1 bg-white dark:bg-slate-800 rounded border border-slate-100 dark:border-slate-700">
                                                            <span className="truncate max-w-[130px] font-medium flex items-center gap-1">
                                                                <span className={`w-2 h-2 rounded-full ${
                                                                    rule.color === 'red' ? 'bg-red-500' :
                                                                    rule.color === 'green' ? 'bg-green-500' :
                                                                    rule.color === 'blue' ? 'bg-blue-500' : 'bg-yellow-500'
                                                                }`}></span>
                                                                {rule.column} {rule.operator === 'equal' ? '=' : rule.operator === 'contains' ? 'cont.' : rule.operator === 'greater' ? '>' : '<'} "{rule.value}"
                                                            </span>
                                                            <button
                                                                onClick={() => setCfRules(prev => prev.filter((_, i) => i !== idx))}
                                                                className="text-red-500 text-[12px] hover:underline font-bold"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Añadir Regla de Formato */}
                                            <button
                                                onClick={() => {
                                                    const cols = selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || [];
                                                    if (cols.length > 0) {
                                                        const newRule = {
                                                            column: cols[0],
                                                            operator: 'equal',
                                                            value: '',
                                                            color: 'red'
                                                        };
                                                        setCfRules(prev => [...prev, newRule]);
                                                    }
                                                }}
                                                className="w-full text-center py-1.5 border border-dashed rounded-lg text-[9px] font-bold text-primary hover:bg-primary/5 transition-colors"
                                            >
                                                + Añadir Regla de Formato
                                            </button>

                                            {/* Configurar reglas de formato condicional */}
                                            {cfRules.map((rule, idx) => (
                                                <div key={idx} className="grid grid-cols-4 gap-1 p-1.5 bg-slate-50 dark:bg-slate-900 rounded border border-slate-100 dark:border-slate-800/80 text-[8px]">
                                                    <select
                                                        value={rule.column}
                                                        onChange={e => setCfRules(prev => prev.map((r, i) => i === idx ? { ...r, column: e.target.value } : r))}
                                                        className="p-1 bg-white dark:bg-slate-850 border rounded text-[8px] text-slate-850 dark:text-white"
                                                    >
                                                        {(selectedColumns[activeEditingComponent] || DEFAULT_COLUMNS[activeEditingComponent] || []).map(c => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                    <select
                                                        value={rule.operator}
                                                        onChange={e => setCfRules(prev => prev.map((r, i) => i === idx ? { ...r, operator: e.target.value } : r))}
                                                        className="p-1 bg-white dark:bg-slate-850 border rounded text-[8px] text-slate-850 dark:text-white"
                                                    >
                                                        <option value="equal">Igual</option>
                                                        <option value="contains">Contiene</option>
                                                        <option value="greater">Mayor</option>
                                                        <option value="less">Menor</option>
                                                    </select>
                                                    <input
                                                        type="text"
                                                        value={rule.value}
                                                        onChange={e => setCfRules(prev => prev.map((r, i) => i === idx ? { ...r, value: e.target.value } : r))}
                                                        placeholder="Valor..."
                                                        className="p-1 bg-white dark:bg-slate-800 border rounded text-[8px] w-full"
                                                    />
                                                    <select
                                                        value={rule.color}
                                                        onChange={e => setCfRules(prev => prev.map((r, i) => i === idx ? { ...r, color: e.target.value as any } : r))}
                                                        className="p-1 bg-white dark:bg-slate-850 border rounded text-[8px] text-slate-850 dark:text-white"
                                                    >
                                                        <option value="red">Rojo</option>
                                                        <option value="green">Verde</option>
                                                        <option value="blue">Azul</option>
                                                        <option value="yellow">Amarillo</option>
                                                    </select>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-450">
                                    <span className="material-symbols-outlined text-[36px] mb-2 text-slate-300">analytics</span>
                                    <p className="text-[11px] leading-relaxed">Selecciona un componente de la izquierda para configurar sus visuales y cálculos.</p>
                                </div>
                            )}
                        </div>
                    </div>
                ) : config.isMocked && mode === 'view' ? (
                    // ==========================================
                    // LOCAL INTERACTIVE VIEW-ONLY VISOR
                    // ==========================================
                    <div id="print-local-report-content" className="flex flex-col flex-1 p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-10rem)]">
                        
                        {/* Interactive Page/Tab Selector and Export dropdown inside Canvas */}
                        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-700 pb-3 no-print">
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setLocalActiveTab('resumen')}
                                    className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${localActiveTab === 'resumen' 
                                        ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                                >
                                    <span className="material-symbols-outlined text-[16px]">dashboard</span>
                                    Resumen Ejecutivo
                                </button>
                                {selectedComponents.includes('scurve') && (
                                    <button
                                        onClick={() => setLocalActiveTab('scurve')}
                                        className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${localActiveTab === 'scurve' 
                                            ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">show_chart</span>
                                        Curva S Avance
                                    </button>
                                )}
                                {selectedComponents.includes('intermediate') && (
                                    <button
                                        onClick={() => setLocalActiveTab('intermediate')}
                                        className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${localActiveTab === 'intermediate' 
                                            ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">pending_actions</span>
                                        Planif. Intermedia
                                    </button>
                                )}
                                {selectedComponents.includes('ppc') && (
                                    <button
                                        onClick={() => setLocalActiveTab('ppc')}
                                        className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${localActiveTab === 'ppc' 
                                            ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">playlist_add_check</span>
                                        Cumplimiento PPC
                                    </button>
                                )}
                                {selectedComponents.includes('quality') && (
                                    <button
                                        onClick={() => setLocalActiveTab('quality')}
                                        className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${localActiveTab === 'quality' 
                                            ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">gpp_good</span>
                                        Gestión Calidad
                                    </button>
                                )}
                                {selectedComponents.includes('subcontracts') && (
                                    <button
                                        onClick={() => setLocalActiveTab('subcontracts')}
                                        className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${localActiveTab === 'subcontracts' 
                                            ? 'bg-primary text-white shadow-md shadow-primary/20' 
                                            : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/60'}`}
                                    >
                                        <span className="material-symbols-outlined text-[16px]">receipt_long</span>
                                        Subcontrato
                                    </button>
                                )}
                            </div>

                            {/* Export dropdown */}
                            <div className="relative group">
                                <button className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all select-none">
                                    <span className="material-symbols-outlined text-[18px]">download</span>
                                    Exportar Reporte
                                    <span className="material-symbols-outlined text-[16px]">keyboard_arrow_down</span>
                                </button>
                                <div className="absolute right-0 mt-1.5 w-44 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 py-1 font-sans">
                                    <button onClick={handleExportPDF} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[16px] text-rose-500">picture_as_pdf</span>
                                        Exportar a PDF
                                    </button>
                                    <button onClick={handleExportHTML} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[16px] text-blue-500">html</span>
                                        Descargar HTML
                                    </button>
                                    <button onClick={handleExportCSV} className="w-full text-left px-4 py-2.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[16px] text-emerald-500">table_rows</span>
                                        Descargar CSV (Datos)
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Render Tab Contents */}
                        {localActiveTab === 'resumen' && (
                            <div className="space-y-6 animate-fade-in">
                                {/* Top KPI Row */}
                                {selectedComponents.includes('kpi') && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                                        <div className="glass-card p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:scale-[1.02] transition-all duration-300">
                                            <div className="size-12 rounded-xl bg-blue-500/10 text-primary flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[30px]">construction</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Avance Físico</span>
                                                <span className="text-xl font-black text-slate-850 dark:text-slate-100">{projectProgress.toFixed(2)}%</span>
                                                <span className="text-[9px] text-slate-400 block mt-0.5">Global Ponderado</span>
                                            </div>
                                        </div>

                                        <div className="glass-card p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:scale-[1.02] transition-all duration-300">
                                            <div className="size-12 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[30px]">task_alt</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">PPC Válido</span>
                                                <span className="text-xl font-black text-slate-850 dark:text-slate-100">{ppcMetrics.ppcValid.toFixed(1)}%</span>
                                                <span className="text-[9px] text-emerald-500 block mt-0.5">Last Planner Standard</span>
                                            </div>
                                        </div>

                                        <div className="glass-card p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:scale-[1.02] transition-all duration-300">
                                            <div className="size-12 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[30px]">payments</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Pagos Aprobados</span>
                                                <span className="text-xl font-black text-slate-850 dark:text-slate-100">{formatCLP(financialMetrics.approved)}</span>
                                                <span className="text-[9px] text-slate-400 block mt-0.5">Neto Subcontratos</span>
                                            </div>
                                        </div>

                                        <div className="glass-card p-5 rounded-2xl shadow-sm flex items-center gap-4 hover:scale-[1.02] transition-all duration-300">
                                            <div className="size-12 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[30px]">account_balance_wallet</span>
                                            </div>
                                            <div>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Garantía Retenida</span>
                                                <span className="text-xl font-black text-slate-850 dark:text-slate-100">{formatCLP(financialMetrics.retentions)}</span>
                                                <span className="text-[9px] text-slate-400 block mt-0.5">Retención Fija 5%</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Main Charts Grid inside Resumen tab */}
                                {widgets.length > 0 ? (
                                    <div className="flex flex-wrap gap-5 w-full">
                                        {widgets.map((widget) => {
                                            let widgetSourceRows: any[] = [];
                                            if (widget.componentId === 'kpi') widgetSourceRows = kpiRows;
                                            else if (widget.componentId === 'scurve') widgetSourceRows = scurveRows;
                                            else if (widget.componentId === 'intermediate') widgetSourceRows = intermediateRows;
                                            else if (widget.componentId === 'ppc') widgetSourceRows = ppcRows;
                                            else if (widget.componentId === 'quality') widgetSourceRows = qualityRows;
                                            else if (widget.componentId === 'subcontracts') widgetSourceRows = subcontractRows;

                                            const widgetRows = processCalculations(widget.componentId, widgetSourceRows);
                                            const widgetCols = selectedColumns[widget.componentId] || DEFAULT_COLUMNS[widget.componentId] || [];

                                            let gridWidth = 'w-full';
                                            if (widget.width === '1/2') gridWidth = 'w-full md:w-[calc(50%-10px)]';
                                            if (widget.width === '1/3') gridWidth = 'w-full md:w-[calc(33.3%-14px)]';

                                            return (
                                                <div 
                                                    key={widget.id} 
                                                    className={`${gridWidth} glass-card p-5 rounded-2xl shadow-sm flex flex-col min-h-[340px]`}
                                                >
                                                    <div className="mb-3 border-b border-slate-100 dark:border-slate-800/80 pb-1.5">
                                                        <span className="text-[8px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest block">
                                                            {widget.componentId === 'kpi' && 'Resumen Ejecutivo (KPIs)'}
                                                            {widget.componentId === 'scurve' && 'Planificación Gantt'}
                                                            {widget.componentId === 'intermediate' && 'Restricciones'}
                                                            {widget.componentId === 'ppc' && 'PPC Semanal'}
                                                            {widget.componentId === 'quality' && 'Control Calidad'}
                                                            {widget.componentId === 'subcontracts' && 'Subcontratos'}
                                                        </span>
                                                        <h5 className="text-[10px] font-extrabold text-slate-880 dark:text-slate-205 truncate">
                                                            {widget.type === 'bar-vertical' && 'Barras V'}
                                                            {widget.type === 'bar-horizontal' && 'Barras H'}
                                                            {widget.type === 'pie' && 'Gráfico Circular'}
                                                            {widget.type === 'matrix' && 'Matriz Jerárquica'}
                                                            {` por ${widget.xAxisKey || 'General'}`}
                                                        </h5>
                                                    </div>
                                                    <div className="flex-1 w-full min-h-[250px] flex flex-col justify-center">
                                                        {renderWidgetChart(widget, widgetRows, widgetCols)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {selectedComponents.includes('scurve') && scurvePoints.length > 0 && (
                                            <div className="glass-card p-5 rounded-2xl shadow-sm">
                                                <h4 className="text-xs font-bold text-slate-850 dark:text-slate-150 uppercase tracking-wider mb-4">Curva S de Avance de Obra</h4>
                                                <div className="h-60 w-full">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <AreaChart data={scurvePoints}>
                                                            <defs>
                                                                <linearGradient id="colorPlan" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                                                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                                                </linearGradient>
                                                                <linearGradient id="colorReal" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                                                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                                                </linearGradient>
                                                            </defs>
                                                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                            <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#888888" />
                                                            <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} stroke="#888888" />
                                                            <Tooltip />
                                                            <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                                                            <Area name="Progreso Programado" type="monotone" dataKey="plan" stroke="#3b82f6" fillOpacity={1} fill="url(#colorPlan)" strokeWidth={2} />
                                                            <Area name="Progreso Real" type="monotone" dataKey="real" stroke="#10b981" fillOpacity={1} fill="url(#colorReal)" strokeWidth={2} />
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        )}

                                        {selectedComponents.includes('ppc') && ppcTrendData.length > 0 && (
                                            <div className="glass-card p-5 rounded-2xl shadow-sm">
                                                <h4 className="text-xs font-bold text-slate-850 dark:text-slate-150 uppercase tracking-wider mb-4">Cumplimiento de Planificación (PPC)</h4>
                                                <div className="h-60 w-full">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart data={ppcTrendData}>
                                                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                            <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#888888" />
                                                            <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} stroke="#888888" />
                                                            <Tooltip />
                                                            <Legend verticalAlign="top" height={36} iconType="rect" wrapperStyle={{ fontSize: '11px' }} />
                                                            <Bar name="Porcentaje PPC" dataKey="PPC" radius={[4, 4, 0, 0]}>
                                                                {ppcTrendData.map((entry, index) => (
                                                                    <Cell key={`cell-${index}`} fill={entry.PPC >= 85 ? '#10b981' : '#f59e0b'} />
                                                                ))}
                                                            </Bar>
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {localActiveTab === 'scurve' && (
                            <div className="glass-card p-6 rounded-2xl shadow-sm space-y-4 animate-fade-in">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-850 dark:text-slate-100">Curva S e Históricos de Obra</h3>
                                    <p className="text-xs text-slate-500">Mapeo del avance real físico de obra contra la línea de planificación de contrato.</p>
                                </div>
                                <div className="h-96 w-full">
                                    {scurvePoints.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={scurvePoints}>
                                                <defs>
                                                    <linearGradient id="colorPlanTab" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                                    </linearGradient>
                                                    <linearGradient id="colorRealTab" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#888888" />
                                                <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} stroke="#888888" />
                                                <Tooltip />
                                                <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                                                <Area name="Progreso Programado Contractual" type="monotone" dataKey="plan" stroke="#3b82f6" fillOpacity={1} fill="url(#colorPlanTab)" strokeWidth={3} />
                                                <Area name="Progreso Real Físico" type="monotone" dataKey="real" stroke="#10b981" fillOpacity={1} fill="url(#colorRealTab)" strokeWidth={3} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-xs text-slate-400">No hay tareas programadas.</div>
                                    )}
                                </div>
                                {renderCustomDataTable('scurve', scurveRows)}
                            </div>
                        )}

                        {localActiveTab === 'intermediate' && (
                            <div className="glass-card p-6 rounded-2xl shadow-sm space-y-4 animate-fade-in">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-850 dark:text-slate-100">Restricciones de Planificación Intermedia (Lookahead)</h3>
                                    <p className="text-xs text-slate-500">Listado de impedimentos y restricciones críticas ordenadas por fecha requerida.</p>
                                </div>
                                {renderCustomDataTable('intermediate', intermediateRows)}
                            </div>
                        )}

                        {localActiveTab === 'ppc' && (
                            <div className="glass-card p-6 rounded-2xl shadow-sm space-y-6 animate-fade-in">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-850 dark:text-slate-100">Evaluación de Porcentaje de Plan Completado (PPC)</h3>
                                    <p className="text-xs text-slate-500">Métricas analíticas del Last Planner System basadas en el cumplimiento semanal de compromisos.</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                    <div className="glass-card p-4 rounded-xl shadow-sm">
                                        <span className="text-[10px] font-bold text-slate-400 block uppercase">PPC Promedio Válido</span>
                                        <span className="text-2xl font-black text-primary block mt-1">{ppcMetrics.ppcValid.toFixed(2)}%</span>
                                        <span className="text-[9px] text-slate-400 block mt-1">Excluye asignaciones pendientes de evaluación</span>
                                    </div>
                                    <div className="glass-card p-4 rounded-xl shadow-sm">
                                        <span className="text-[10px] font-bold text-slate-400 block uppercase">PPC Bruto Auxiliar</span>
                                        <span className="text-2xl font-black text-slate-850 dark:text-slate-200 block mt-1">{ppcMetrics.ppcGross.toFixed(2)}%</span>
                                        <span className="text-[9px] text-slate-400 block mt-1">Calculado con respecto al total de asignaciones</span>
                                    </div>
                                    <div className="glass-card p-4 rounded-xl shadow-sm">
                                        <span className="text-[10px] font-bold text-slate-400 block uppercase">Asignaciones de Obra</span>
                                        <span className="text-2xl font-black text-emerald-500 block mt-1">{ppcMetrics.executed} / {ppcMetrics.executed + ppcMetrics.notExecuted}</span>
                                        <span className="text-[9px] text-emerald-500 block mt-1">Asignaciones semanales ejecutadas con éxito</span>
                                    </div>
                                </div>

                                <div className="h-80 w-full">
                                    {ppcTrendData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={ppcTrendData}>
                                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#888888" />
                                                <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} stroke="#888888" />
                                                <Tooltip />
                                                <Bar name="Porcentaje PPC" dataKey="PPC" radius={[4, 4, 0, 0]}>
                                                    {ppcTrendData.map((entry, index) => (
                                                        <Cell key={`cell-tab-${index}`} fill={entry.PPC >= 85 ? '#10b981' : '#f59e0b'} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-xs text-slate-400">No hay planes semanales cerrados.</div>
                                    )}
                                </div>
                                {renderCustomDataTable('ppc', ppcRows)}
                            </div>
                        )}

                        {localActiveTab === 'quality' && (
                            <div className="glass-card p-6 rounded-2xl shadow-sm space-y-6 animate-fade-in">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-850 dark:text-slate-100">Estado de Protocolos de Calidad</h3>
                                    <p className="text-xs text-slate-500">Métricas analíticas sobre aprobaciones y rechazos de calidad en terreno.</p>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                                    <div className="h-56 flex items-center justify-center">
                                        {qualityMetrics.total > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={[
                                                            { name: 'Cerrados', value: qualityMetrics.closedCount, color: '#10b981' },
                                                            { name: 'Abiertos', value: qualityMetrics.openCount, color: '#3b82f6' },
                                                            { name: 'Rechazados', value: qualityMetrics.rejectedCount, color: '#f43f5e' }
                                                        ]}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={60}
                                                        outerRadius={80}
                                                        paddingAngle={5}
                                                        dataKey="value"
                                                    >
                                                        {[
                                                            { color: '#10b981' },
                                                            { color: '#3b82f6' },
                                                            { color: '#f43f5e' }
                                                        ].map((entry, index) => (
                                                            <Cell key={`cell-pie-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="text-xs text-slate-400">No hay protocolos guardados.</div>
                                        )}
                                    </div>

                                    <div className="lg:col-span-2 space-y-4">
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Detalle de Estados</h4>
                                        <div className="space-y-3">
                                            {[
                                                { label: 'Protocolos Cerrados', count: qualityMetrics.closedCount, color: 'bg-emerald-500', pct: qualityMetrics.total > 0 ? (qualityMetrics.closedCount / qualityMetrics.total) * 100 : 0 },
                                                { label: 'Protocolos En Proceso / Abiertos', count: qualityMetrics.openCount, color: 'bg-blue-500', pct: qualityMetrics.total > 0 ? (qualityMetrics.openCount / qualityMetrics.total) * 100 : 0 },
                                                { label: 'Protocolos Rechazados / Con Observaciones', count: qualityMetrics.rejectedCount, color: 'bg-rose-500', pct: qualityMetrics.total > 0 ? (qualityMetrics.rejectedCount / qualityMetrics.total) * 100 : 0 }
                                            ].map((item, idx) => (
                                                <div key={idx} className="space-y-1">
                                                    <div className="flex justify-between text-xs font-medium">
                                                        <div className="flex items-center gap-2">
                                                            <span className={`size-3 rounded-full ${item.color}`} />
                                                            <span className="text-slate-700 dark:text-slate-300">{item.label}</span>
                                                        </div>
                                                        <span className="text-slate-900 dark:text-white font-bold">{item.count} ({item.pct.toFixed(1)}%)</span>
                                                    </div>
                                                    <div className="w-full bg-slate-100 dark:bg-slate-700 h-2 rounded-full overflow-hidden">
                                                        <div className={`h-full ${item.color}`} style={{ width: `${item.pct}%` }} />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                {renderCustomDataTable('quality', qualityRows)}
                            </div>
                        )}

                        {localActiveTab === 'subcontracts' && (
                            <div className="glass-card p-6 rounded-2xl shadow-sm space-y-6 animate-fade-in">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-850 dark:text-slate-100">Subcontrato</h3>
                                    <p className="text-xs text-slate-500">Comparativa analítica entre montos totales contratados y estados de pago aprobados/liquidados por contratista.</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="h-80">
                                        {subcontractChartData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={subcontractChartData}>
                                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                    <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#888888" />
                                                    <YAxis tick={{ fontSize: 9 }} stroke="#888888" />
                                                    <Tooltip formatter={(value) => formatCLP(value as number)} />
                                                    <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: '11px' }} />
                                                    <Bar name="Monto Contratado" dataKey="Contratado" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                                                    <Bar name="Monto Aprobado/Pagado" dataKey="Visado" fill="#10b981" radius={[3, 3, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="h-full flex items-center justify-center text-xs text-slate-400">No hay contratos registrados.</div>
                                        )}
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Consolidado Financiero</h4>
                                        <div className="divide-y divide-slate-100 dark:divide-slate-700">
                                            {[
                                                { label: 'Total Contratado', val: financialMetrics.contracted, color: 'text-slate-850 dark:text-slate-150' },
                                                { label: 'Total Pagado Visado', val: financialMetrics.approved, color: 'text-emerald-600 dark:text-emerald-400' },
                                                { label: 'Retenciones Acumuladas (Garantías)', val: financialMetrics.retentions, color: 'text-purple-600 dark:text-purple-400' },
                                                { label: 'Saldo Pendiente por Liquidar', val: Math.max(0, financialMetrics.contracted - financialMetrics.approved), color: 'text-blue-600 dark:text-blue-400 font-bold' }
                                            ].map((item, idx) => (
                                                <div key={idx} className="flex justify-between py-3 text-xs">
                                                    <span className="text-slate-500">{item.label}</span>
                                                    <span className={`${item.color} font-black`}>{formatCLP(item.val)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                {renderCustomDataTable('subcontracts', subcontractRows)}
                            </div>
                        )}

                    </div>
                ) : config.accessToken ? (
                    <>
                        {hasWarningBanner && (
                            <div className="w-full bg-amber-500/10 text-amber-500 border-b border-amber-500/20 px-6 py-2 flex items-center gap-2 z-20 absolute top-0 left-0 right-0 h-10 select-none">
                                <span className="material-symbols-outlined text-[16px]">lock</span>
                                Diseñador en modo Solo Lectura: No tiene permisos de modificación.
                            </div>
                        )}
                        <div
                            ref={embedContainerRef}
                            className={`w-full h-full absolute ${hasWarningBanner ? 'top-10' : 'inset-0'} rounded-b-2xl`}
                            style={{ height: hasWarningBanner ? 'calc(100% - 2.5rem)' : '100%' }}
                        />
                    </>
                ) : (
                    <div className="max-w-xl text-center p-8 space-y-5 animate-fade-in mx-auto mt-16">
                        <div className="size-16 rounded-2xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center mx-auto shadow-inner">
                            <span className="material-symbols-outlined text-[36px]">analytics</span>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                Módulo de Inteligencia de Negocios Activo
                            </h3>
                            <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                                La plataforma analítica de K-Lyapp está completamente lista. Seleccione un reporte personalizado en el catálogo superior o use el diseñador para crear visualizaciones interactivas de avance y PPC.
                            </p>
                        </div>

                        <div className="bg-slate-900 text-slate-200 text-left p-4 rounded-xl font-mono text-[11px] space-y-1 shadow-inner border border-slate-800">
                            <p className="text-slate-500"># Infraestructura Analítica Activa</p>
                            <p><span className="text-emerald-400">Esquema SQL:</span> bi (7 vistas analíticas)</p>
                            <p><span className="text-emerald-400">Optimización:</span> Vista materializada de Curva S activa</p>
                            <p><span className="text-emerald-400">Gobernanza:</span> Dataset "K-Lyapp" y RLS por Obra habilitada</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
